import React from "react";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  verifyBeforeUpdateEmail,
} from "firebase/auth";
import { collection, doc, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useAnglerCatalog, searchSpecies } from "../data/anglerCatalog";
import { C } from "../theme";
import Card from "../components/Card";
import Button from "../components/Button";
import Badge from "../components/Badge";
import ScoreRing from "../components/ScoreRing";
import CondRow from "../components/CondRow";
import HotspotMap from "../components/HotspotMap";
import CountryFlag from "../components/CountryFlag";
import ProfileAvatar from "../components/ProfileAvatar";
import ProfilePhotoModal from "../components/ProfilePhotoModal";
import { getProfilePhotoUrl, uploadProfilePhoto, removeProfilePhoto } from "../services/profilePhoto";
import { isValidCountry } from "../data/countries";
import { WATER_TYPE_OPTIONS, SUBTYPE_OPTIONS, buildWaterTypePayload } from "../data/waterTypes";
import useTideStage from "../hooks/useTideStage";
import useFlowStage from "../hooks/useFlowStage";
import useCloudCover from "../hooks/useCloudCover";
import { getSeasonPhase } from "../utils/seasonPhase";
import useMoonPhase from "../hooks/useMoonPhase";
import MoonPhaseIcon from "../components/MoonPhaseIcon";
import useSolunarMajor from "../hooks/useSolunarMajor";
import SolunarStatusIcon from "../components/SolunarStatusIcon";
import useWaterTemp from "../hooks/useWaterTemp";
import useWind from "../hooks/useWind";
import useBarometricPressure from "../hooks/useBarometricPressure";
import usePatternTier, { TIER_THRESHOLDS } from "../hooks/usePatternTier";
import { useSessionContext } from "../context/SessionContext";
import { buildConditionsSnapshot } from "../utils/conditionsSnapshot";
import { APP_VERSION } from "../appVersion";
import { requestPreparationScore, requestPatternInsight, requestTournamentScout, ProRequiredError } from "../services/aiClient";
import TideForecastModal from "../components/TideForecastModal";
import ScheduleTripModal from "../components/ScheduleTripModal";
import PlannedTripsModal from "../components/PlannedTripsModal";
import {
  watchScheduledTrips,
  saveScheduledTrip,
  deleteScheduledTrip,
  markTripAutoStarted,
  ensureNotificationPermission,
  reconcileTripReminders,
} from "../services/tripScheduler";
import { openStoreListing } from "../services/appReview";
import { getSubscriptionView, maxTrackedSpecies, isProActive } from "../utils/featureGate";
import { Capacitor } from "@capacitor/core";
import CollapsibleSection from "../components/CollapsibleSection";

// Inside the native app (Play Store / App Store build) the PWA "Add to Home
// Screen" flow is irrelevant, so the Install App preference is only shown on
// the web PWA.
const IS_NATIVE_APP = Capacitor?.isNativePlatform?.() === true;

// A labelled row of small pill chips, used by the Tournament Scout brief to
// render time windows / species / lures compactly. Renders nothing when empty.
function ScoutChipRow({ label, items }) {
  const list = (Array.isArray(items) ? items : []).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 9, color: C.seafoam, letterSpacing: 1, textTransform: "uppercase", marginBottom: 3 }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {list.map((item, i) => (
          <span
            key={`${item}-${i}`}
            style={{
              padding: "3px 8px",
              borderRadius: 999,
              border: `1px solid ${C.border}`,
              background: `${C.accent}1f`,
              color: C.white,
              fontSize: 10,
              fontWeight: 600,
            }}
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

// Auto-start dedup guard at MODULE scope (not a component ref) so it survives
// DashboardScreen remounts — ending a session returns to the dashboard, which
// remounts this screen; a per-instance ref would reset and re-fire the
// auto-start for a trip still inside its window.
const _firedAutoStarts = new Set();

// Catch-sessions (trips with at least one logged catch) needed before the AI
// Pattern Insight model actually runs. Matches the `effectiveTier >= 3` gate.
const AI_UNLOCK_SESSIONS = TIER_THRESHOLDS[3]; // 10

// Plain-language tier ladder shown in the Pattern Insight badge tooltip so
// anglers (especially Free, who watch this fill up as they log) understand
// what the numbers mean. Reflects actual shipped behavior — Tier 2 is folded
// into Seeding, so AI turns on at 10.
const PATTERN_TIER_TOOLTIP = [
  "AI Pattern Insight grows as you log trips with at least one catch",
  "(skunked trips don't count):",
  "",
  "• Locked — 0 trips",
  "• Seeding — 1–9 trips (building your fishing fingerprint)",
  "• Active — 10–19 trips (AI insights turn on)",
  "• Compound — 20+ trips (deepest multi-factor analysis)",
  "",
  "Progress counts on every plan.",
  "Viewing the AI insights themselves requires Pro.",
].join("\n");



export default function DashboardScreen({
  userId,
  onStart,
  onViewTrips,
  onOpenGoPro,
  showGoProButton,
  setShowGoProButton,
  goProMaxClipMins,
  setGoProMaxClipMins,
  onSignOut,
  showConditions,
  setShowConditions,
  soundAlerts,
  setSoundAlerts,
  vibrationAlerts,
  setVibrationAlerts,
  themeMode,
  onThemeModeChange,
  fontSizeMode,
  onFontSizeModeChange,
  location,
  geoStatus,
  onRequestGeoPermission,
  userProfile,
  onLeaveTournament,
  leavingTournament,
  isOffline,
  forceOffline,
  tournamentAreaRestricted,
  tournamentDates,
  tournamentStatus,
  tournamentLiveStartMs,
  autoStartTrip = null,
  onAutoStartConsumed,
}) {
  const detectStandalone = React.useCallback(() => {
    if (typeof window === "undefined") return false;
    const mediaStandalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
    const iosStandalone = window.navigator?.standalone === true;
    return mediaStandalone || iosStandalone;
  }, []);

  const [showSettings, setShowSettings] = React.useState(false);
  const [showGuide, setShowGuide] = React.useState(false);
  const [showProfilePhoto, setShowProfilePhoto] = React.useState(false);
  const profilePhotoInputRef = React.useRef(null);
  const [photoUploading, setPhotoUploading] = React.useState(false);
  const [photoError, setPhotoError] = React.useState("");

  const profilePhotoUrl = getProfilePhotoUrl(userProfile);
  const profileDisplayName = [userProfile?.firstName, userProfile?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const handleProfilePhotoPick = async (e) => {
    const file = e?.target?.files?.[0];
    if (e?.target) e.target.value = ""; // allow re-picking the same file later
    if (!file) return;
    setPhotoError("");
    setPhotoUploading(true);
    try {
      await uploadProfilePhoto(file);
    } catch (err) {
      setPhotoError(err?.message || "Couldn't update your photo. Please try again.");
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleProfilePhotoRemove = async () => {
    setPhotoError("");
    setPhotoUploading(true);
    try {
      await removeProfilePhoto();
    } catch (err) {
      setPhotoError(err?.message || "Couldn't remove your photo. Please try again.");
    } finally {
      setPhotoUploading(false);
    }
  };
  const [showStationPicker, setShowStationPicker] = React.useState(false);
  const [showGagePicker, setShowGagePicker] = React.useState(false);
  const [showUserPreferences, setShowUserPreferences] = React.useState(false);
  const { species: speciesCatalog, loading: speciesLoading } = useAnglerCatalog();
  const [preferredSpeciesIds, setPreferredSpeciesIds] = React.useState([]);
  const [preferredSpeciesLoaded, setPreferredSpeciesLoaded] = React.useState(false);
  const [speciesQuery, setSpeciesQuery] = React.useState("");
  const [shareVideos, setShareVideos] = React.useState(false);
  const [tripSmartSuggest, setTripSmartSuggest] = React.useState(true);
  // Max species the angler may track, per their subscription (Free vs Pro).
  const maxSpecies = maxTrackedSpecies(userProfile);
  React.useEffect(() => {
    if (!userId) return undefined;
    const unsubscribe = onSnapshot(
      doc(db, "userPreferences", userId),
      (snap) => {
        const data = snap.exists() ? (snap.data() || {}) : {};
        // Curated species have historically lived under two fields: this
        // dashboard picker wrote `fishSpecies`, while enrollment + the Catch
        // Log use `fishSpeciesIds`. Merge both on read (and write both on save)
        // so every screen stays consistent.
        const toIds = (arr) =>
          Array.isArray(arr) ? arr.map((id) => String(id || "").trim()).filter(Boolean) : [];
        const ids = Array.from(new Set([...toIds(data.fishSpecies), ...toIds(data.fishSpeciesIds)]));
        setPreferredSpeciesIds(ids);
        setShareVideos(Boolean(data.shareVideos));
        setTripSmartSuggest(data.tripSmartSuggest !== false);
        setPreferredSpeciesLoaded(true);
      },
      (e) => {
        console.warn("[Dashboard] failed to load userPreferences:", e?.message || e);
        setPreferredSpeciesLoaded(true);
      }
    );
    return () => unsubscribe();
  }, [userId]);
  const persistPreferredSpecies = React.useCallback(async (next) => {
    setPreferredSpeciesIds(next);
    try {
      // Write both fields so the Catch Log, Sessions filter, and this picker
      // all read the same curated list.
      await setDoc(
        doc(db, "userPreferences", userId),
        { fishSpecies: next, fishSpeciesIds: next },
        { merge: true }
      );
    } catch (e) {
      console.warn("[Dashboard] failed to save fishSpecies:", e?.message || e);
    }
  }, [userId]);
  const addPreferredSpecies = React.useCallback((speciesId) => {
    if (!userId || !speciesId) return;
    if (preferredSpeciesIds.includes(speciesId)) return;
    if (preferredSpeciesIds.length >= maxSpecies) return; // subscription cap
    setSpeciesQuery("");
    persistPreferredSpecies([...preferredSpeciesIds, speciesId]);
  }, [userId, preferredSpeciesIds, maxSpecies, persistPreferredSpecies]);
  const removePreferredSpecies = React.useCallback((speciesId) => {
    if (!userId || !speciesId) return;
    persistPreferredSpecies(preferredSpeciesIds.filter((id) => id !== speciesId));
  }, [userId, preferredSpeciesIds, persistPreferredSpecies]);
  const toggleShareVideos = React.useCallback(async (next) => {
    if (!userId) return;
    setShareVideos(next);
    try {
      await setDoc(doc(db, "userPreferences", userId), { shareVideos: next }, { merge: true });
    } catch (e) {
      console.warn("[Dashboard] failed to save shareVideos:", e?.message || e);
    }
  }, [userId]);

  // ── Scheduled trips (planned future sessions + reminders) ──
  const [scheduledTrips, setScheduledTrips] = React.useState([]);
  const [showScheduleTrip, setShowScheduleTrip] = React.useState(false);
  const [showPlannedTrips, setShowPlannedTrips] = React.useState(false);
  const [editingTrip, setEditingTrip] = React.useState(null);
  const [recentSpots, setRecentSpots] = React.useState([]);
  React.useEffect(() => {
    if (!userId) { setScheduledTrips([]); return undefined; }
    return watchScheduledTrips(userId, (trips) => {
      setScheduledTrips(trips);
      // Re-arm reminders for server-created (tournament auto-enroll) trips this
      // device never scheduled itself. Idempotent by notificationId.
      reconcileTripReminders(trips);
    });
  }, [userId]);
  // Re-arm server-created trip reminders on every foreground. onSnapshot only
  // fires on data changes, so a member who enrolled while the app was closed
  // needs this to schedule the local reminders before startAt on next launch.
  const scheduledTripsRef = React.useRef([]);
  React.useEffect(() => { scheduledTripsRef.current = scheduledTrips; }, [scheduledTrips]);
  React.useEffect(() => {
    const rearm = () => {
      if (!document.hidden) reconcileTripReminders(scheduledTripsRef.current);
    };
    window.addEventListener("focus", rearm);
    document.addEventListener("visibilitychange", rearm);
    return () => {
      window.removeEventListener("focus", rearm);
      document.removeEventListener("visibilitychange", rearm);
    };
  }, []);
  // Unique recent session spots for the schedule modal's "Recent" location tab.
  // Loaded lazily when the modal opens (labelled by the session's tide station).
  const loadRecentSpots = React.useCallback(async () => {
    if (!userId) return;
    try {
      const q = query(collection(db, "users", userId, "sessions"), orderBy("startedAt", "desc"), limit(30));
      const snap = await getDocs(q);
      const seen = new Set();
      const spots = [];
      snap.docs.forEach((d) => {
        const s = d.data() || {};
        const loc = s.location;
        if (!loc || typeof loc.lat !== "number" || typeof loc.lng !== "number") return;
        const label = String(s.tideStationName || "").trim();
        const key = label ? `st:${label.toLowerCase()}` : `c:${loc.lat.toFixed(3)},${loc.lng.toFixed(3)}`;
        if (seen.has(key)) return;
        seen.add(key);
        spots.push({
          key,
          label: label || `${loc.lat.toFixed(3)}, ${loc.lng.toFixed(3)}`,
          lat: loc.lat,
          lng: loc.lng,
          stationId: s.tideStationId || null,
        });
      });
      setRecentSpots(spots.slice(0, 12));
    } catch (e) {
      console.warn("[Dashboard] loadRecentSpots failed:", e?.message || e);
    }
  }, [userId]);
  const toggleTripSmartSuggest = React.useCallback(async (next) => {
    if (!userId) return;
    setTripSmartSuggest(next);
    try {
      await setDoc(doc(db, "userPreferences", userId), { tripSmartSuggest: next }, { merge: true });
    } catch (e) {
      console.warn("[Dashboard] failed to save tripSmartSuggest:", e?.message || e);
    }
  }, [userId]);
  const openNewTrip = React.useCallback(() => {
    setEditingTrip(null);
    setShowPlannedTrips(false);
    setShowScheduleTrip(true);
    loadRecentSpots();
    // Prompt for notification permission up front (background) so the OS dialog
    // resolves while the angler fills the form — never gate saving on it.
    ensureNotificationPermission().catch(() => {});
  }, [loadRecentSpots]);
  const openEditTrip = React.useCallback((trip) => {
    setEditingTrip(trip);
    setShowPlannedTrips(false);
    setShowScheduleTrip(true);
    loadRecentSpots();
    ensureNotificationPermission().catch(() => {});
  }, [loadRecentSpots]);
  const handleSaveTrip = React.useCallback(async (input) => {
    await saveScheduledTrip(userId, input);
  }, [userId]);
  const handleDeleteTrip = React.useCallback(async (trip) => {
    try {
      await deleteScheduledTrip(userId, trip);
    } catch (e) {
      console.warn("[Dashboard] delete trip failed:", e?.message || e);
    }
  }, [userId]);
  const nextTrip = React.useMemo(() => {
    const now = Date.now();
    return scheduledTrips
      .filter((t) => t.repeat !== "once" || (t.startAt?.toMillis ? t.startAt.toMillis() : 0) >= now)
      .sort((a, b) => (a.startAt?.toMillis?.() || 0) - (b.startAt?.toMillis?.() || 0))[0] || null;
  }, [scheduledTrips]);
  const nextTripView = React.useMemo(() => {
    if (!nextTrip) return null;
    const d = nextTrip.startAt?.toDate ? nextTrip.startAt.toDate() : null;
    const time = d
      ? new Intl.DateTimeFormat(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" }).format(d)
      : "";
    const title = nextTrip.name ? `${time} — ${nextTrip.name}` : time || "Planned trip";
    const speciesLabels = (nextTrip.targetSpeciesIds || [])
      .map((id) => speciesCatalog.find((s) => s.id === id)?.label)
      .filter(Boolean);
    const subBits = [];
    if (nextTrip.location?.label) subBits.push(nextTrip.location.label);
    if (speciesLabels.length) subBits.push(speciesLabels.slice(0, 3).join(", "));
    const reminder = "You'll be notified when it starts";
    let countdown = "";
    if (d) {
      const ms = d.getTime() - Date.now();
      if (ms > 0) {
        const mins = Math.round(ms / 60000);
        countdown = mins < 60 ? `in ${mins}m` : mins < 1440 ? `in ${Math.round(mins / 60)}h` : `in ${Math.round(mins / 1440)}d`;
      } else {
        countdown = "now";
      }
    }
    return { title, sub: subBits.join(" · "), reminder, countdown };
  }, [nextTrip, speciesCatalog]);
  const [showConditionsPreferences, setShowConditionsPreferences] = React.useState(false);
  const [showTournamentConfiguration, setShowTournamentConfiguration] = React.useState(false);
  const [showTideForecast, setShowTideForecast] = React.useState(false);
  const [showAccountSettings, setShowAccountSettings] = React.useState(false);
  const [accountSection, setAccountSection] = React.useState(null); // null | 'password' | 'email'
  const [acctCurrentPassword, setAcctCurrentPassword] = React.useState("");
  const [acctNewPassword, setAcctNewPassword] = React.useState("");
  const [acctShowCurrentPw, setAcctShowCurrentPw] = React.useState(false);
  const [acctShowNewPw, setAcctShowNewPw] = React.useState(false);
  const [acctNewEmail, setAcctNewEmail] = React.useState("");
  const [acctEmailCurrentPassword, setAcctEmailCurrentPassword] = React.useState("");
  const [acctShowEmailPw, setAcctShowEmailPw] = React.useState(false);
  const [acctLoading, setAcctLoading] = React.useState(false);
  const [acctError, setAcctError] = React.useState("");
  const [acctSuccess, setAcctSuccess] = React.useState("");

  const acctEmailUser = auth.currentUser?.email || "";
  const acctLoginProvider = (userProfile?.loginProvider || "").toLowerCase();
  const acctIsPasswordUser = acctLoginProvider === "password" || acctLoginProvider === "";

  function acctPasswordErrors(pw) {
    const e = [];
    if (pw.length < 6) e.push("At least 6 characters");
    if (pw.length >= 72) e.push("Fewer than 72 characters");
    if (!/\d/.test(pw)) e.push("A number");
    if (!/[!@#$%^&*()\-_=+[\]{};':",.<>/?\\|`~]/.test(pw)) e.push("A special character");
    if (!/[A-Z]/.test(pw)) e.push("An uppercase letter");
    if (!/[a-z]/.test(pw)) e.push("A lowercase letter");
    if (acctEmailUser && pw.toLowerCase() === acctEmailUser.trim().toLowerCase()) e.push("Different from your email");
    return e;
  }

  const acctPwRequirements = [
    { label: "At least 6 characters", pass: acctNewPassword.length >= 6 },
    { label: "Fewer than 72 characters", pass: acctNewPassword.length > 0 && acctNewPassword.length < 72 },
    { label: "Includes a number", pass: /\d/.test(acctNewPassword) },
    { label: "Includes a special character", pass: /[!@#$%^&*()\-_=+[\]{};':",.<>/?\\|`~]/.test(acctNewPassword) },
    { label: "Uppercase and lowercase letters", pass: /[A-Z]/.test(acctNewPassword) && /[a-z]/.test(acctNewPassword) },
    { label: "Different from your email", pass: acctEmailUser.length > 0 && acctNewPassword.length > 0 && acctNewPassword.toLowerCase() !== acctEmailUser.toLowerCase() },
  ];
  const acctNewPwValid = acctNewPassword.length > 0 && acctPasswordErrors(acctNewPassword).length === 0;

  const handleAccountSectionOpen = (section) => {
    setAccountSection(section);
    setAcctCurrentPassword("");
    setAcctNewPassword("");
    setAcctNewEmail("");
    setAcctEmailCurrentPassword("");
    setAcctError("");
    setAcctSuccess("");
    setAcctShowCurrentPw(false);
    setAcctShowNewPw(false);
    setAcctShowEmailPw(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setAcctError("");
    setAcctSuccess("");
    if (!acctCurrentPassword) { setAcctError("Current password is required."); return; }
    if (!acctNewPwValid) { setAcctError("Fix all password requirements before continuing."); return; }
    setAcctLoading(true);
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, acctCurrentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, acctNewPassword);
      setAcctSuccess("Password updated successfully.");
      setAcctCurrentPassword("");
      setAcctNewPassword("");
    } catch (err) {
      if (err?.code === "auth/wrong-password" || err?.code === "auth/invalid-credential") {
        setAcctError("Current password is incorrect.");
      } else if (err?.code === "auth/too-many-requests") {
        setAcctError("Too many attempts. Please wait and try again.");
      } else {
        setAcctError(err?.message || "Password change failed. Please try again.");
      }
    } finally {
      setAcctLoading(false);
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setAcctError("");
    setAcctSuccess("");
    const trimmedEmail = acctNewEmail.trim().toLowerCase();
    if (!trimmedEmail) { setAcctError("New email address is required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) { setAcctError("Enter a valid email address."); return; }
    if (!acctEmailCurrentPassword) { setAcctError("Current password is required to change your email."); return; }
    setAcctLoading(true);
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, acctEmailCurrentPassword);
      await reauthenticateWithCredential(user, credential);
      await verifyBeforeUpdateEmail(user, trimmedEmail);
      setAcctSuccess(`Verification link sent to ${trimmedEmail}. Click it to confirm your new email.`);
      setAcctNewEmail("");
      setAcctEmailCurrentPassword("");
    } catch (err) {
      if (err?.code === "auth/wrong-password" || err?.code === "auth/invalid-credential") {
        setAcctError("Current password is incorrect.");
      } else if (err?.code === "auth/email-already-in-use") {
        setAcctError("That email is already in use by another account.");
      } else if (err?.code === "auth/too-many-requests") {
        setAcctError("Too many attempts. Please wait and try again.");
      } else {
        setAcctError(err?.message || "Email change failed. Please try again.");
      }
    } finally {
      setAcctLoading(false);
    }
  };
  const [showSolunarInfo, setShowSolunarInfo] = React.useState(false);
  const [showAreaRestrictionInfo, setShowAreaRestrictionInfo] = React.useState(false);
  const [showPreparationScore, setShowPreparationScore] = React.useState(true);
  const [showPlannedTripsSection, setShowPlannedTripsSection] = React.useState(false);
  const [showPrepDrivers, setShowPrepDrivers] = React.useState(false);
  const [showTripInfo, setShowTripInfo] = React.useState(false);
  const [showPatternInsight, setShowPatternInsight] = React.useState(true);
  const [showPatternTierInfo, setShowPatternTierInfo] = React.useState(false);
  const [patternInsight, setPatternInsight] = React.useState(null);
  const [patternInsightLoading, setPatternInsightLoading] = React.useState(false);
  const [patternInsightError, setPatternInsightError] = React.useState(null);
  const [startError, setStartError] = React.useState(null);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = React.useState(() =>
    typeof window !== "undefined" ? window.__pwaInstallPrompt || null : null
  );
  const [isStandaloneInstalled, setIsStandaloneInstalled] = React.useState(() => detectStandalone());
  const [tempUnit, setTempUnit] = React.useState(() => {
    try {
      return localStorage.getItem("the99.tempUnit") === "C" ? "C" : "F";
    } catch {
      return "F";
    }
  });

  const [selectedStationId, setSelectedStationId] = React.useState(() => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const id = localStorage.getItem("the99.stationId");
      const day = localStorage.getItem("the99.stationId.day");
      // A manual station override only lasts for the calendar day it was
      // picked; the next day the app returns to GPS auto-selection.
      if (id && day === today) return id;
      localStorage.removeItem("the99.stationId");
      localStorage.removeItem("the99.stationId.day");
      return null;
    } catch {
      return null;
    }
  });

  React.useEffect(() => {
    try {
      if (selectedStationId) {
        localStorage.setItem("the99.stationId", selectedStationId);
        localStorage.setItem("the99.stationId.day", new Date().toISOString().slice(0, 10));
      } else {
        localStorage.removeItem("the99.stationId");
        localStorage.removeItem("the99.stationId.day");
      }
    } catch {}
  }, [selectedStationId]);

  // If the app is left open across midnight, drop a stale manual override the
  // next time it regains focus so we fall back to GPS auto-selection.
  React.useEffect(() => {
    const clearIfStale = () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const day = localStorage.getItem("the99.stationId.day");
        if (localStorage.getItem("the99.stationId") && day && day !== today) {
          setSelectedStationId(null);
        }
      } catch {}
    };
    window.addEventListener("focus", clearIfStale);
    document.addEventListener("visibilitychange", clearIfStale);
    return () => {
      window.removeEventListener("focus", clearIfStale);
      document.removeEventListener("visibilitychange", clearIfStale);
    };
  }, []);

  React.useEffect(() => {
    try {
      localStorage.setItem("the99.tempUnit", tempUnit);
    } catch {}
  }, [tempUnit]);

  // Manual USGS gage override for freshwater (analog of selectedStationId).
  // Persists across days — an angler's home lake/river gage is stable.
  const [selectedGageId, setSelectedGageId] = React.useState(() => {
    try { return localStorage.getItem("the99.usgsSiteId") || null; } catch { return null; }
  });
  React.useEffect(() => {
    try {
      if (selectedGageId) localStorage.setItem("the99.usgsSiteId", selectedGageId);
      else localStorage.removeItem("the99.usgsSiteId");
    } catch {}
  }, [selectedGageId]);

  React.useEffect(() => {
    const updateStandalone = () => setIsStandaloneInstalled(detectStandalone());
    const onInstallable = () => setDeferredInstallPrompt(window.__pwaInstallPrompt || null);
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      window.__pwaInstallPrompt = event;
      setDeferredInstallPrompt(event);
    };
    const onInstalled = () => {
      setDeferredInstallPrompt(null);
      updateStandalone();
    };

    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("pwa-installed", onInstalled);
    window.addEventListener("visibilitychange", updateStandalone);

    const mq = window.matchMedia ? window.matchMedia("(display-mode: standalone)") : null;
    if (mq && mq.addEventListener) {
      mq.addEventListener("change", updateStandalone);
    }

    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("pwa-installed", onInstalled);
      window.removeEventListener("visibilitychange", updateStandalone);
      if (mq && mq.removeEventListener) {
        mq.removeEventListener("change", updateStandalone);
      }
    };
  }, [detectStandalone]);

  const [pulling, setPulling] = React.useState(false);
  const [pullReady, setPullReady] = React.useState(false);
  const [manualRefreshing, setManualRefreshing] = React.useState(false);
  const [spinStep, setSpinStep] = React.useState(0);
  const [showTopFadeHint, setShowTopFadeHint] = React.useState(false);
  const [showBottomFadeHint, setShowBottomFadeHint] = React.useState(false);
  const [prepScoreData, setPrepScoreData] = React.useState({
    score: 0,
    confidence: "loading",
    summary: "Waiting for API response...",
    drivers: [],
  });
  const [prepScoreMeta, setPrepScoreMeta] = React.useState({
    loading: true,
    source: "pending",
    promptVersion: "",
    requestId: "",
    error: "",
  });
  const scrollRef = React.useRef(null);
  const touchStartYRef = React.useRef(0);

  const prepConfidence = (prepScoreData.confidence || "").toLowerCase();
  const isPrepConfidenceLoading = prepScoreMeta.loading || prepConfidence === "loading";
  const prepConfidenceColor =
    isPrepConfidenceLoading
      ? C.muted
      : prepConfidence === "high"
      ? C.accent
      : prepConfidence === "low"
      ? C.warning
      : C.gold;
  const prepConfidenceLabel = isPrepConfidenceLoading
    ? "LOADING CONFIDENCE"
    : `${(prepConfidence || "unknown").toUpperCase()} CONFIDENCE`;

  const updateScrollFadeHints = React.useCallback(() => {
    const node = scrollRef.current;
    if (!node) {
      setShowTopFadeHint(false);
      setShowBottomFadeHint(false);
      return;
    }

    const maxScrollTop = Math.max(0, node.scrollHeight - node.clientHeight);
    const hasOverflow = maxScrollTop > 2;
    const nextShowTop = hasOverflow && node.scrollTop > 2;
    const nextShowBottom = hasOverflow && node.scrollTop < maxScrollTop - 2;

    setShowTopFadeHint(nextShowTop);
    setShowBottomFadeHint(nextShowBottom);
  }, []);

  const handleTouchStart = (e) => {
    if (scrollRef.current?.scrollTop !== 0) return;
    touchStartYRef.current = e.touches[0]?.clientY ?? 0;
    setPulling(true);
    setPullReady(false);
  };

  const handleTouchMove = (e) => {
    if (!pulling) return;
    const currentY = e.touches[0]?.clientY ?? 0;
    const delta = Math.max(0, currentY - touchStartYRef.current);
    setPullReady(delta > 60);
  };

  const handleTouchEnd = () => {
    if (!pulling) return;
    if (pullReady) {
      refreshTide();
    }
    setPulling(false);
    setPullReady(false);
  };

  const gpsLabel =
    geoStatus === "available"
      ? location
        ? `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`
        : "acquiring…"
      : geoStatus === "denied"
      ? "permission denied"
      : geoStatus === "unsupported"
      ? "unsupported"
      : "waiting…";

  // Anglers may fish saltwater, freshwater, or both. When they fish both, we
  // ask once per day which kind of fishing they're doing (the daily prompt
  // below), because tide conditions are saltwater-only. `activeWaterType`
  // resolves to today's choice, or the single enrolled type otherwise.
  const enrolledWaterTypes = React.useMemo(() => {
    const arr = Array.isArray(userProfile?.waterTypes) ? userProfile.waterTypes : null;
    const list = arr && arr.length > 0
      ? arr
      : (userProfile?.waterType ? [userProfile.waterType] : []);
    return list.map((w) => String(w || "").toLowerCase()).filter(Boolean);
  }, [userProfile?.waterTypes, userProfile?.waterType]);
  const fishesBoth =
    enrolledWaterTypes.includes("saltwater") && enrolledWaterTypes.includes("freshwater");
  // Which water the angler is fishing THIS SESSION (saltwater/freshwater), for
  // anglers enrolled in both. Stored in sessionStorage (not per calendar day)
  // so the prompt re-appears on every fresh app launch / login — but not when
  // merely navigating within a session. Also cleared on sign-out (App.jsx) and
  // whenever the angler changes their water TYPES in Preferences below.
  const [todaysFishingMode, setTodaysFishingMode] = React.useState(() => {
    try { return sessionStorage.getItem("the99.fishingMode") || null; } catch { return null; }
  });
  const chooseFishingMode = React.useCallback((mode) => {
    setTodaysFishingMode(mode);
    try {
      if (mode) {
        sessionStorage.setItem("the99.fishingMode", mode);
      } else {
        sessionStorage.removeItem("the99.fishingMode");
      }
    } catch {}
  }, []);
  const activeWaterType = fishesBoth
    ? todaysFishingMode
    : (enrolledWaterTypes[0] || "");
  // Show the prompt only to anglers who fish both and haven't picked yet this
  // session. Single-water-type anglers never see it.
  const needsFishingModePrompt = fishesBoth && !todaysFishingMode;

  // ── My Species (subscription-limited, water-type aware) ─────────────────
  // Resolve the angler's chosen species to catalog entries, then split by the
  // water they're fishing this session so the picker only surfaces what's
  // relevant. Search results are likewise scoped to the active water type.
  const selectedSpeciesEntries = React.useMemo(
    () => preferredSpeciesIds
      .map((id) => speciesCatalog.find((s) => s.id === id))
      .filter(Boolean),
    [preferredSpeciesIds, speciesCatalog]
  );
  const visibleSelectedSpecies = React.useMemo(() => {
    if (!activeWaterType) return selectedSpeciesEntries;
    return selectedSpeciesEntries.filter(
      (sp) => Array.isArray(sp.waters) && sp.waters.includes(activeWaterType)
    );
  }, [selectedSpeciesEntries, activeWaterType]);
  const otherWaterSelectedCount = selectedSpeciesEntries.length - visibleSelectedSpecies.length;
  const speciesSearchResults = React.useMemo(() => {
    const q = speciesQuery.trim();
    if (!q) return [];
    return searchSpecies(speciesCatalog, q, activeWaterType)
      .filter((sp) => !preferredSpeciesIds.includes(sp.id));
  }, [speciesQuery, speciesCatalog, activeWaterType, preferredSpeciesIds]);
  const speciesAtLimit = preferredSpeciesIds.length >= maxSpecies;
  const activeWaterLabel =
    activeWaterType === "freshwater" ? "freshwater" : activeWaterType === "saltwater" ? "saltwater" : "";

  // Freshwater anglers have no tide station — NOAA CO-OPS tide predictions
  // only cover coastal/saltwater stations. Skip the fetch and surface the
  // condition (and its Preferences picker) as inactive for them.
  const tideInactive = String(activeWaterType || "").toLowerCase() === "freshwater";

  const { tideStage, stationName, stationId: autoStationId, stations, nextTide, loading: tideLoading, error: tideError, refresh: refreshTide } = useTideStage({
    lat: location?.lat,
    lng: location?.lng,
    enabled: !!location && !tideInactive && !needsFishingModePrompt,
    stationId: selectedStationId,
  });
  const moon = useMoonPhase(location);
  const solunarMajor = useSolunarMajor(location);
  const waterTemp = useWaterTemp(location, tempUnit);
  const wind = useWind(location);
  const barometric = useBarometricPressure(location);

  // ── Freshwater (lake/stream) conditions ──────────────────────────────
  // Freshwater has no tide station; instead we use the nearest USGS gage for a
  // flow/level trend + real water temperature, plus cloud cover and a season/
  // spawn phase. `freshwaterActive` mirrors `tideInactive` for readability.
  const freshwaterActive = tideInactive;
  const enrolledSubTypes = React.useMemo(() => {
    const arr = Array.isArray(userProfile?.subTypes) ? userProfile.subTypes : [];
    return arr.map((t) => String(t || "").toLowerCase()).filter(Boolean);
  }, [userProfile?.subTypes]);
  // River → flow-primary; lake-only → level-primary; both → prefer flow (river).
  const preferLevel = enrolledSubTypes.includes("lake") && !enrolledSubTypes.includes("river");
  const primaryFreshSubType = enrolledSubTypes.includes("river") ? "river" : "lake";

  // ── Editable water-type / sub-type selections (User Preferences) ─────────
  // Anglers can update where they fish after enrollment. Writes to the same
  // users/{uid} fields the Enrollment screen sets; the onSnapshot listener in
  // App.jsx refreshes userProfile, so the editor and all downstream Conditions
  // update immediately.
  const [waterTypeSaving, setWaterTypeSaving] = React.useState(false);
  const [waterTypeError, setWaterTypeError] = React.useState("");
  const persistWaterSelections = React.useCallback(
    async (nextWaterTypes, nextSubTypes) => {
      if (!userId) return;
      setWaterTypeSaving(true);
      setWaterTypeError("");
      try {
        await setDoc(
          doc(db, "users", userId),
          { ...buildWaterTypePayload(nextWaterTypes, nextSubTypes), updatedAt: serverTimestamp() },
          { merge: true }
        );
      } catch (e) {
        console.warn("[Dashboard] failed to save water types:", e?.message || e);
        setWaterTypeError("Couldn't save — check your connection and try again.");
      } finally {
        setWaterTypeSaving(false);
      }
    },
    [userId]
  );
  const toggleWaterTypePref = React.useCallback(
    (id) => {
      const has = enrolledWaterTypes.includes(id);
      const nextWaterTypes = has
        ? enrolledWaterTypes.filter((w) => w !== id)
        : [...enrolledWaterTypes, id];
      // Removing a water type drops its sub-types so none are orphaned.
      const removedSubIds = has ? (SUBTYPE_OPTIONS[id] || []).map((o) => o.id) : [];
      const nextSubTypes = enrolledSubTypes.filter((s) => !removedSubIds.includes(s));
      persistWaterSelections(nextWaterTypes, nextSubTypes);
      // Changing the set of water TYPES invalidates this session's saltwater/
      // freshwater choice — clear it so the "fishing today?" prompt re-asks
      // (it only re-shows when the angler still fishes both).
      chooseFishingMode(null);
    },
    [enrolledWaterTypes, enrolledSubTypes, persistWaterSelections, chooseFishingMode]
  );
  const toggleSubTypePref = React.useCallback(
    (id) => {
      const has = enrolledSubTypes.includes(id);
      const nextSubTypes = has
        ? enrolledSubTypes.filter((s) => s !== id)
        : [...enrolledSubTypes, id];
      persistWaterSelections(enrolledWaterTypes, nextSubTypes);
    },
    [enrolledWaterTypes, enrolledSubTypes, persistWaterSelections]
  );

  const flow = useFlowStage({
    lat: location?.lat,
    lng: location?.lng,
    enabled: !!location && freshwaterActive && !needsFishingModePrompt,
    siteId: selectedGageId,
    preferLevel,
  });
  const cloudCover = useCloudCover(location, { enabled: freshwaterActive });

  // Effective water temperature in °F — prefer the real USGS gage reading for
  // freshwater, else the useWaterTemp hook estimate. Drives the season phase
  // and the displayed Water Temp row for freshwater.
  const baseWaterTempF = waterTemp?.tempC != null ? Math.round((waterTemp.tempC * 9) / 5 + 32) : null;
  const gageWaterTempF = freshwaterActive && flow.waterTempF != null ? flow.waterTempF : null;
  const effWaterTempF = gageWaterTempF ?? baseWaterTempF;
  const seasonPhase = React.useMemo(
    () => (freshwaterActive ? getSeasonPhase(effWaterTempF, primaryFreshSubType) : null),
    [freshwaterActive, effWaterTempF, primaryFreshSubType]
  );
  // Water Temp row view: show the gage temperature for freshwater when present.
  const waterTempView = gageWaterTempF != null
    ? {
        label: `${tempUnit === "C" ? Math.round(((gageWaterTempF - 32) * 5) / 9) : gageWaterTempF}\u00b0${tempUnit}`,
        status: "good",
      }
    : { label: waterTemp.label, status: waterTemp.status };

  const { startSession, sessionId, lastConditionLogAt, wasResumed, acknowledgeResume } = useSessionContext();
  const patternTier = usePatternTier(userId);
  const anyConditionLoading = tideLoading || wind.loading || barometric.loading || waterTemp.loading || (freshwaterActive && flow.loading);
  const isRefreshingConditions = manualRefreshing && anyConditionLoading;

  // Freshwater fallback "station" when no USGS gage is nearby: a coarse
  // geo-cell (~11 km) derived from GPS. This lets freshwater anglers still get
  // a Prep Score for their local waters — sessions and history scope to the
  // same cell, mirroring how a gage/tide-station scopes them elsewhere. Stable
  // across trips launched from roughly the same area.
  const freshwaterGeoStationId = React.useMemo(() => {
    if (!freshwaterActive) return null;
    if (!location || !Number.isFinite(location.lat) || !Number.isFinite(location.lng)) return null;
    return `fwgeo:${location.lat.toFixed(1)},${location.lng.toFixed(1)}`;
  }, [freshwaterActive, location]);

  // Effective "station" id/name for AI Pattern Insight + Prep Score. Saltwater
  // uses the tide station; freshwater uses the USGS gage (or, when none is in
  // range, the geo-cell fallback) so sessions and history are scoped per body
  // of water the same way.
  const effectiveStationId = freshwaterActive
    ? (selectedGageId || flow.siteId || freshwaterGeoStationId || null)
    : (selectedStationId || autoStationId || null);
  const effectiveStationName = freshwaterActive
    ? (flow.siteName || (freshwaterGeoStationId ? "your local waters" : null))
    : (stationName || null);
  // True when the shown station/gage was hand-picked rather than GPS-auto.
  const isManualStation = freshwaterActive
    ? (Boolean(selectedGageId) && selectedGageId !== flow.siteId)
    : (Boolean(selectedStationId) && selectedStationId !== autoStationId);

  // Map center for the AI Hot Spots map when there are no plotted spots yet.
  // Saltwater → the effective tide station's coordinates; freshwater (no
  // station) or as a fallback → the device's current GPS location.
  const hotspotCenter = React.useMemo(() => {
    const st = Array.isArray(stations)
      ? stations.find((s) => s && s.id === effectiveStationId)
      : null;
    if (st && Number.isFinite(Number(st.lat)) && Number.isFinite(Number(st.lng))) {
      return { lat: Number(st.lat), lng: Number(st.lng) };
    }
    if (location && Number.isFinite(location.lat) && Number.isFinite(location.lng)) {
      return { lat: location.lat, lng: location.lng };
    }
    return null;
  }, [stations, effectiveStationId, location]);

  const hotspotCenterLabel = React.useMemo(() => {
    if (freshwaterActive) return flow.siteName || "your area";
    return stationName || "your area";
  }, [freshwaterActive, flow.siteName, stationName]);

  // Fetch Pattern Insight whenever the user signs in or the station
  // changes. Server-side day-bucket cache plus the client-side cache
  // in aiClient.js keep this cheap on revisits.
  //
  // Tier gate: Tier 0 (empty) and Tier 1 (seeding, 1–4 catch-sessions)
  // do not call the model — the UI surfaces a local seeding placeholder
  // instead. We also skip the call while the tier hook is still loading
  // so we don't waste a request that we'd then hide.
  React.useEffect(() => {
    if (!userId || !effectiveStationId) {
      setPatternInsight(null);
      setPatternInsightError(null);
      setPatternInsightLoading(false);
      return;
    }
    if (patternTier.loading) return;
    if (patternTier.effectiveTier < 3) {
      // Below the Active-Insights threshold — clear any stale result and
      // skip the network call entirely.
      setPatternInsight(null);
      setPatternInsightError(null);
      setPatternInsightLoading(false);
      return;
    }
    let cancelled = false;
    setPatternInsightLoading(true);
    setPatternInsightError(null);
    requestPatternInsight({
      userId,
      targetStationId: effectiveStationId,
      targetStationName: effectiveStationName,
      isOffline: !!isOffline || !!forceOffline,
    })
      .then((data) => {
        if (cancelled) return;
        setPatternInsight(data || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setPatternInsight(null);
        setPatternInsightError(err?.message || String(err));
      })
      .finally(() => {
        if (cancelled) return;
        setPatternInsightLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, effectiveStationId, stationName, isOffline, forceOffline, patternTier.loading, patternTier.effectiveTier]);

  const lastSessionLogLabel = React.useMemo(() => {
    if (!sessionId || !lastConditionLogAt) return "not active";
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(lastConditionLogAt));
  }, [sessionId, lastConditionLogAt]);

  const tournamentDisplayName = userProfile?.tournamentName || userProfile?.tournamentId || "";
  const teamDisplayName = userProfile?.teamName || "";
  const fishermanTournamentName = tournamentDisplayName;
  const fishermanTeamName = teamDisplayName;
  const showTeamTripInfo = Boolean(teamDisplayName);

  const normalizedTournamentStatus = String(tournamentStatus || "").trim().toLowerCase();
  const isTournamentArchived =
    normalizedTournamentStatus === "archived" || normalizedTournamentStatus === "ended";

  const tournamentStart = tournamentDates?.startDate ? new Date(tournamentDates.startDate) : null;
  const tournamentEndRaw = tournamentDates?.endDate ? new Date(tournamentDates.endDate) : null;
  // If the end date is exactly midnight UTC (i.e. the TD only specified a
  // calendar date), interpret it as end-of-day so the tournament window
  // covers the full day. Same-day tournaments would otherwise read as
  // already ended the moment the day begins.
  const tournamentEnd = (() => {
    if (!tournamentEndRaw) return null;
    const ms = tournamentEndRaw.getTime();
    const isMidnightUtc =
      tournamentEndRaw.getUTCHours() === 0 &&
      tournamentEndRaw.getUTCMinutes() === 0 &&
      tournamentEndRaw.getUTCSeconds() === 0 &&
      tournamentEndRaw.getUTCMilliseconds() === 0;
    return isMidnightUtc ? new Date(ms + 24 * 60 * 60 * 1000 - 1) : tournamentEndRaw;
  })();
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 15000);
    return () => clearInterval(id);
  }, []);
  const tournamentDatesDefined = Boolean(tournamentStart || tournamentEnd);
  const tournamentNotYetStarted = tournamentStart && now < tournamentStart;
  const tournamentEnded = tournamentEnd && now > tournamentEnd;
  // "Live" state is driven by the notification flow: it flips on once the
  // T-15 countdown elapses and stays on until tournament_ended arrives (or
  // until the published end-of-day passes, whichever is first). We also
  // require the tournament's published date window to be current AND the
  // live start to fall inside that window so a stale event from a prior
  // run doesn't override a rescheduled tournament.
  const liveStartInWindow = Boolean(
    tournamentLiveStartMs &&
      (!tournamentStart || tournamentLiveStartMs >= tournamentStart.getTime() - 60_000) &&
      (!tournamentEnd || tournamentLiveStartMs <= tournamentEnd.getTime())
  );
  const tournamentInProgress = Boolean(
    liveStartInWindow &&
      now.getTime() >= tournamentLiveStartMs &&
      !tournamentNotYetStarted &&
      !tournamentEnded
  );
  React.useEffect(() => {
    console.log("[Dashboard] live-state:", {
      tournamentLiveStartMs,
      liveStartInWindow,
      tournamentNotYetStarted,
      tournamentEnded,
      tournamentInProgress,
      tournamentStart: tournamentStart?.toISOString() || null,
      tournamentEnd: tournamentEnd?.toISOString() || null,
      now: now.toISOString(),
    });
  }, [tournamentLiveStartMs, liveStartInWindow, tournamentNotYetStarted, tournamentEnded, tournamentInProgress, tournamentStart, tournamentEnd, now]);
  const tournamentDateMismatch = Boolean(fishermanTournamentName && (tournamentNotYetStarted || tournamentEnded));
  const formatTournamentDate = (d) =>
    d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
  const tournamentDateRangeLabel = (() => {
    const s = formatTournamentDate(tournamentStart);
    const e = formatTournamentDate(tournamentEnd);
    if (s && e) return `${s} – ${e}`;
    if (s) return `Starts ${s}`;
    if (e) return `Ends ${e}`;
    return null;
  })();
  const showTournamentTripInfo = Boolean(tournamentDisplayName) && !isTournamentArchived;
  const showGpsTripInfo = true;
  const hasRowAfterDate = showGpsTripInfo || showTournamentTripInfo || showTeamTripInfo;
  const hasRowAfterGps = showTournamentTripInfo || showTeamTripInfo;
  const hasRowAfterTournament = showTeamTripInfo;

  // ── Pre-tournament prefishing prompt (Phase 1) ──────────────────────────
  // When the angler has an enrolled tournament starting within the next week
  // (and it hasn't started yet), offer to tag today's trips as "prefishing"
  // for that tournament so a later scouting/strategy report can use them.
  const upcomingTournamentId = userProfile?.tournamentId || "";
  const daysUntilTournament = tournamentStart
    ? Math.ceil((tournamentStart.getTime() - now.getTime()) / 86400000)
    : null;
  const hasUpcomingTournament = Boolean(
    fishermanTournamentName &&
    upcomingTournamentId &&
    !isTournamentArchived &&
    tournamentNotYetStarted &&
    daysUntilTournament != null &&
    daysUntilTournament <= 7
  );
  const prefishDayKey = React.useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, [now]);
  const prefishLsKey = upcomingTournamentId ? `the99.prefish.${upcomingTournamentId}.${prefishDayKey}` : "";
  const [prefishAnswer, setPrefishAnswer] = React.useState(null);
  React.useEffect(() => {
    if (!prefishLsKey) { setPrefishAnswer(null); return; }
    try { setPrefishAnswer(localStorage.getItem(prefishLsKey) || null); }
    catch { setPrefishAnswer(null); }
  }, [prefishLsKey]);
  const answerPrefish = React.useCallback((ans) => {
    setPrefishAnswer(ans);
    try { if (prefishLsKey) localStorage.setItem(prefishLsKey, ans); } catch {}
  }, [prefishLsKey]);
  // The tournament id today's trips should be tagged for (or null).
  const prefishingTodayFor = prefishAnswer === "yes" ? upcomingTournamentId : null;

  // ── Tournament Scout (Phase 2): on-demand pre-tournament brief ──────────
  // Builds a scouting report from the angler's prefishing + historical
  // sessions at this water. Pro-gated server-side. Defined after
  // effectiveStationId/Name, upcomingTournamentId, and fishermanTournamentName
  // so all are in scope for the request.
  const [scoutData, setScoutData] = React.useState(null);
  const [scoutLoading, setScoutLoading] = React.useState(false);
  const [scoutError, setScoutError] = React.useState("");
  const [scoutProRequired, setScoutProRequired] = React.useState(false);
  const fetchTournamentScout = React.useCallback(async () => {
    if (!userId || !upcomingTournamentId) return;
    setScoutLoading(true);
    setScoutError("");
    setScoutProRequired(false);
    try {
      const resp = await requestTournamentScout({
        userId,
        tournamentId: upcomingTournamentId,
        tournamentName: fishermanTournamentName || null,
        targetStationId: effectiveStationId || null,
        targetStationName: effectiveStationName || null,
        isOffline: !!isOffline || !!forceOffline,
      });
      if (resp?.result) {
        setScoutData(resp.result);
      } else {
        setScoutError("Scouting report unavailable — try again.");
      }
    } catch (err) {
      if (err instanceof ProRequiredError) {
        setScoutProRequired(true);
      } else {
        setScoutError(err?.message || "Couldn't build your scouting report.");
      }
    } finally {
      setScoutLoading(false);
    }
  }, [userId, upcomingTournamentId, fishermanTournamentName, effectiveStationId, effectiveStationName, isOffline, forceOffline]);

  const isIosDevice = React.useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return /iphone|ipad|ipod/i.test(navigator.userAgent || "");
  }, []);
  const isAndroidDevice = React.useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return /android/i.test(navigator.userAgent || "");
  }, []);
  const showIosInstallHint = isIosDevice && !isStandaloneInstalled;
  const canPromptInstall = Boolean(deferredInstallPrompt) && !isStandaloneInstalled;

  const handleInstallApp = React.useCallback(async () => {
    if (!deferredInstallPrompt) {
      if (isAndroidDevice) {
        window.alert("Install not ready yet. In Chrome, open menu (⋮) and tap 'Install app' or 'Add to Home screen'. Keep using the app and this button will become active when Chrome provides the native prompt.");
      }
      return;
    }
    try {
      await deferredInstallPrompt.prompt();
      const choice = await deferredInstallPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        setDeferredInstallPrompt(null);
      }
    } catch (err) {
      console.warn("[PWA] install prompt failed", err);
    }
  }, [deferredInstallPrompt, isAndroidDevice]);

  React.useEffect(() => {
    if (manualRefreshing && !anyConditionLoading) {
      setManualRefreshing(false);
    }
  }, [manualRefreshing, anyConditionLoading]);

  React.useEffect(() => {
    if (!isRefreshingConditions) return undefined;
    const interval = setInterval(() => {
      setSpinStep((s) => (s + 1) % 12);
    }, 80);
    return () => clearInterval(interval);
  }, [isRefreshingConditions]);

  const refreshPreparationScore = React.useCallback(async () => {
    if (!userId) return;

    // Pro gate — Preparation Score is a Pro feature (subscriptions.md §3a;
    // featureGate.canUsePreparationScore). Free / Pass-only users never call
    // the backend; the card renders a Pro upsell panel instead (see render).
    if (!getSubscriptionView(userProfile).isProActive) {
      setPrepScoreData({ score: 0, confidence: "low", summary: "", drivers: [] });
      setPrepScoreMeta({
        loading: false,
        source: "pro-gate",
        promptVersion: "",
        requestId: "",
        error: "",
      });
      return;
    }

    // Tier gate — parallels the Pattern Insight gate. Tier 0/1 users get a
    // descriptive placeholder; no backend call, no wasted Firestore reads.
    if (!patternTier.loading && patternTier.effectiveTier < 2) {
      setPrepScoreData({
        score: 0,
        confidence: "low",
        summary:
          patternTier.effectiveTier === 0
            ? "Log a few trips with catches and we'll start scoring how favorable each day is for you."
            : `Building your fingerprint — ${patternTier.catchSessions} / ${patternTier.nextThreshold ?? 5} trips with catches.`,
        drivers: [],
      });
      setPrepScoreMeta({
        loading: false,
        source: "tier-gate",
        promptVersion: "",
        requestId: "",
        error: "",
      });
      return;
    }

    const context = {
      conditions: buildConditionsSnapshot({
        moon,
        solunarMajor,
        waterTemp,
        wind,
        barometric,
        tideStage,
        nextTide,
        waterMovement: freshwaterActive ? flow : null,
        cloudCover: freshwaterActive ? cloudCover : null,
        seasonPhase: freshwaterActive ? seasonPhase : null,
        waterTempF: freshwaterActive ? (flow.waterTempF ?? null) : null,
      }),
      // Station context is REQUIRED by the deterministic Prep Score
      // algorithm — without it the backend returns a locked placeholder.
      // Freshwater resolves this to the USGS gage id, or a GPS geo-cell
      // fallback when no gage is in range.
      targetStationId: effectiveStationId || null,
      targetStationName: effectiveStationName,
      location: location ? { lat: location.lat, lng: location.lng } : null,
      geoStatus,
      profile: {
        role: userProfile?.role || "unknown",
        inTournament: !!userProfile?.inTournament,
        tournamentName: userProfile?.tournamentName || null,
        teamName: userProfile?.teamName || null,
      },
    };

    setPrepScoreMeta((prev) => ({ ...prev, loading: true, error: "" }));

    try {
      const response = await requestPreparationScore({
        userId,
        context,
        forceRegenerate: true,
      });

      if (response?.status !== "ready" || !response?.result) {
        throw new Error("Prep score response was not ready");
      }

      setPrepScoreData({
        score: response.result.score,
        confidence: response.result.confidence,
        summary: response.result.summary,
        drivers: Array.isArray(response.result.drivers) ? response.result.drivers : [],
      });
      setPrepScoreMeta({
        loading: false,
        source: "api",
        promptVersion: response.version?.promptVersion || "",
        requestId: response.requestId || "",
        error: "",
      });
    } catch (err) {
      setPrepScoreData({
        score: 0,
        confidence: "loading",
        summary: "Preparation score unavailable. Check API connection.",
        drivers: [],
      });
      setPrepScoreMeta((prev) => ({
        ...prev,
        loading: false,
        source: "fallback",
        error: err?.message || "Unable to load prep score from API",
      }));
    }
  }, [
    userId,
    patternTier.loading,
    patternTier.effectiveTier,
    patternTier.catchSessions,
    patternTier.nextThreshold,
    userProfile?.subscription,
    moon,
    solunarMajor,
    waterTemp,
    wind,
    barometric,
    tideStage,
    nextTide,
    effectiveStationId,
    stationName,
    effectiveStationName,
    freshwaterActive,
    flow,
    cloudCover,
    seasonPhase,
    location,
    geoStatus,
    userProfile?.role,
    userProfile?.inTournament,
    userProfile?.tournamentName,
    userProfile?.teamName,
  ]);

  React.useEffect(() => {
    if (!userId) return;
    // Re-run when station resolves or tier loads/changes so the score reflects
    // the right water body and the tier gate doesn't show a stale placeholder.
    refreshPreparationScore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, effectiveStationId, patternTier.loading, patternTier.effectiveTier]);

  React.useEffect(() => {
    const node = scrollRef.current;
    if (!node) return undefined;

    const onLayoutChange = () => updateScrollFadeHints();
    const raf = requestAnimationFrame(onLayoutChange);
    window.addEventListener("resize", onLayoutChange);

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(onLayoutChange);
      observer.observe(node);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onLayoutChange);
      observer?.disconnect();
    };
  }, [
    updateScrollFadeHints,
    showTripInfo,
    showPreparationScore,
    showConditions,
    showSettings,
    showStationPicker,
  ]);

  const handleRefreshConditions = () => {
    setManualRefreshing(true);
    refreshTide();
    moon.refresh?.();
    solunarMajor.refresh?.();
    waterTemp.refresh?.();
    wind.refresh?.();
    barometric.refresh?.();
    if (freshwaterActive) {
      flow.refresh?.();
      cloudCover.refresh?.();
    }
    refreshPreparationScore();
  };

  // ── Scheduled-trip auto-start (Tier 1: app open, Tier 2: notification) ────
  // Reusable session start, shared by the Start button and scheduled auto-start.
  const handleStartTrip = React.useCallback(async () => {
    setStartError(null);
    if (sessionId) {
      onStart();
      return sessionId;
    }
    const snapshot = buildConditionsSnapshot({
      moon, solunarMajor, waterTemp, wind, barometric, tideStage, nextTide,
      waterMovement: freshwaterActive ? flow : null,
      cloudCover: freshwaterActive ? cloudCover : null,
      seasonPhase: freshwaterActive ? seasonPhase : null,
      waterTempF: freshwaterActive ? (flow.waterTempF ?? null) : null,
    });
    const createdSessionId = await startSession(
      snapshot,
      location,
      {
        tournamentId: userProfile?.tournamentId || "",
        tournamentName: userProfile?.tournamentName || "",
        teamId: userProfile?.teamId || "",
        teamName: userProfile?.teamName || "",
      },
      {
        tideStationId: effectiveStationId,
        tideStationName: effectiveStationName,
        prefishFor: prefishingTodayFor || null,
      },
      {
        score: prepScoreData?.score ?? null,
        confidence: prepScoreData?.confidence ?? null,
      }
    );
    if (!createdSessionId) {
      setStartError("Could not start session — check Firebase config/rules and try again");
    }
    return createdSessionId;
  }, [
    sessionId, onStart, moon, solunarMajor, waterTemp, wind, barometric, tideStage,
    nextTide, startSession, location, userProfile, selectedStationId, autoStationId,
    stationName, prepScoreData,
    freshwaterActive, flow, cloudCover, seasonPhase, effectiveStationId, effectiveStationName,
    prefishingTodayFor,
  ]);

  const [autoStartedNotice, setAutoStartedNotice] = React.useState("");

  const runAutoStart = React.useCallback(async (trip) => {
    if (sessionId) return; // already in a session — don't double-start
    const created = await handleStartTrip();
    if (created) {
      // Mark a one-off trip consumed so it never auto-starts again (e.g. after
      // the angler ends the session and returns to the dashboard, or on an app
      // restart while still inside the trip's window).
      if (trip?.id && (trip.repeat || "once") === "once") {
        markTripAutoStarted(userId, trip.id);
      }
      setAutoStartedNotice(
        trip?.name
          ? `Auto-started your scheduled session: ${trip.name}.`
          : "Auto-started your scheduled session."
      );
    }
  }, [sessionId, handleStartTrip, userId]);

  // Tier 2: a scheduled-trip notification's "Start" action / tap arrived.
  React.useEffect(() => {
    if (!autoStartTrip) return;
    const key = `notif:${autoStartTrip.tripId || ""}:${autoStartTrip.at || ""}`;
    if (_firedAutoStarts.has(key)) { onAutoStartConsumed?.(); return; }
    _firedAutoStarts.add(key);
    const trip = scheduledTrips.find((t) => t.id === autoStartTrip.tripId) || null;
    runAutoStart(trip).finally(() => onAutoStartConsumed?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStartTrip]);

  // Tier 1: while the dashboard is open, auto-start a one-off scheduled trip
  // when its start time arrives (within a short grace window). Recurring trips
  // rely on the notification's Start action instead.
  React.useEffect(() => {
    if (!scheduledTrips.length) return undefined;
    const AUTO_START_WINDOW_MS = 30 * 60 * 1000;
    const check = () => {
      if (sessionId) return;
      const now = Date.now();
      for (const trip of scheduledTrips) {
        if ((trip.repeat || "once") !== "once") continue;
        if (trip.autoStartedAt) continue; // already consumed (persisted flag)
        const startMs = trip.startAt?.toMillis ? trip.startAt.toMillis() : trip.startAtMs;
        if (!startMs) continue;
        if (now >= startMs && now <= startMs + AUTO_START_WINDOW_MS) {
          const key = `time:${trip.id}:${startMs}`;
          if (_firedAutoStarts.has(key)) continue;
          _firedAutoStarts.add(key);
          runAutoStart(trip);
          break;
        }
      }
    };
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [scheduledTrips, sessionId, runAutoStart]);

  // Auto-dismiss the "session auto-started" confirmation after a few seconds.
  React.useEffect(() => {
    if (!autoStartedNotice) return undefined;
    const t = setTimeout(() => setAutoStartedNotice(""), 6000);
    return () => clearTimeout(t);
  }, [autoStartedNotice]);

  if (showGuide) {
    return (
      <div
        className="hide-scrollbar"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          paddingBottom: "calc(120px + env(safe-area-inset-bottom, 0px))",
          scrollPaddingBottom: "calc(120px + env(safe-area-inset-bottom, 0px))",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{ padding: "18px 18px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, color: C.teal, letterSpacing: 3, marginBottom: 2 }}>THE 99</div>
            <div style={{ fontSize: 20, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700 }}>How to Use</div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowGuide(false)}
            style={{ fontSize: 11 }}
          >
            ← Back to Preparation
          </Button>
        </div>

        <div style={{ margin: "0 14px 14px" }}>
          <Card>
            <div style={{ padding: "14px" }}>
              <div style={{ fontSize: 11, color: C.teal, letterSpacing: 2, marginBottom: 10 }}>QUICK START</div>
              <div style={{ fontSize: 12, color: C.white, lineHeight: 1.6 }}>
                1. Review your Preparation Dashboard and conditions.
                <br />
                2. Tap <strong>Start Session</strong> to begin recording activity.
                <br />
                3. In Session Logging, use <strong>Video Proof</strong> for a certified catch clip.
                <br />
                4. Use <strong>Log Catch</strong> to save species, lure, length, and weight.
                <br />
                5. End session to generate your report and review patterns.
              </div>
            </div>
          </Card>
        </div>

        <div style={{ margin: "0 14px 14px" }}>
          <Card>
            <div style={{ padding: "14px" }}>
              <div style={{ fontSize: 11, color: C.teal, letterSpacing: 2, marginBottom: 10 }}>TIPS</div>
              <div style={{ fontSize: 12, color: C.white, lineHeight: 1.6 }}>
                • Enable GPS for better tide/location data.
                <br />
                • Keep fish + marker visible during Video Proof.
                <br />
                • Use Settings (gear icon) to toggle conditions and station preferences.
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <>
    <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column" }}>
    <div
      className="hide-scrollbar"
      ref={scrollRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onScroll={updateScrollFadeHints}
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        paddingBottom: "calc(132px + env(safe-area-inset-bottom, 0px))",
        scrollPaddingBottom: "calc(132px + env(safe-area-inset-bottom, 0px))",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {pulling && (
        <div
          style={{
            position: "absolute",
            top: 60,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
            color: C.muted,
            fontSize: 11,
          }}
        >
          {pullReady ? "Release to refresh" : "Pull to refresh"}
        </div>
      )}
      <div style={{ padding: "18px 18px 12px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <ProfilePhotoModal
          open={showProfilePhoto}
          onClose={() => setShowProfilePhoto(false)}
          url={profilePhotoUrl}
          name={profileDisplayName}
          teamName={userProfile?.teamName || ""}
        />
        {autoStartedNotice && (
          <div
            role="status"
            onClick={() => setAutoStartedNotice("")}
            style={{
              position: "fixed",
              left: 12,
              right: 12,
              bottom: "calc(80px + env(safe-area-inset-bottom, 0px))",
              zIndex: 60,
              background: C.seafoam,
              color: C.dark,
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 12,
              fontWeight: 700,
              textAlign: "center",
              boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
              cursor: "pointer",
            }}
          >
            {autoStartedNotice}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
            {isValidCountry(userProfile?.country) && (
              <CountryFlag code={userProfile.country} size={18} />
            )}
            <div style={{ fontSize: 11, color: C.teal, letterSpacing: 3 }}>THE 99</div>
          </div>
          <div style={{ fontSize: 20, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700 }}>Preparation Dashboard</div>
          <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {(() => {
              // Source of truth: users/{uid}.subscription, written by
              // paypal_record_subscription / paypal_webhook (or the harness
              // bridge against the emulator). See utils/featureGate.js for
              // tier vocabulary. tournamentPassTier is a separate axis: an
              // angler can hold a pass without holding Pro.
              //
              // Gate the Pro labels on isProActive (tier ∈ PRO_TIERS AND
              // status ∈ ACTIVE_STATUSES), not on tier alone — otherwise
              // a canceled/expired/past_due Pro doc would still render
              // "Pro Monthly" until the doc was deleted. Same for the
              // tournament pass: only show the badge if the pass hasn't
              // expired yet.
              const view = getSubscriptionView(userProfile);
              const passActive =
                view.tournamentPassTier === "annual" &&
                (!view.tournamentPassExpiresMs || view.tournamentPassExpiresMs > Date.now());
              let planLabel = "Angler";
              // Both paid Pro tiers (monthly + annual) display as a neutral
              // "Pro Angler" badge. We intentionally avoid surfacing the
              // billing cadence ("Monthly"/"Annual") in the UI so the app
              // doesn't allude to in-app payment tiers.
              if (view.isProActive && (view.tier === "pro_monthly" || view.tier === "pro_annual")) planLabel = "Pro Angler";
              else if (passActive) planLabel = "Tournament Pass";
              // Per-plan styling so the pill conveys the subscription type by
              // color, not just text. Free is intentionally neutral/muted so
              // it reads as the baseline tier; paid tiers get an accent.
              const planStyle =
                planLabel === "Pro Angler"
                  ? { border: `1px solid ${C.gold}`, background: `${C.gold}1F`, color: C.gold }
                  : planLabel === "Tournament Pass"
                  ? { border: `1px solid ${C.warning}`, background: `${C.warning}1F`, color: C.warning }
                  : { border: `1px solid ${C.muted}66`, background: `${C.muted}1F`, color: C.muted };
              return (
                <span
                  id="dash-plan-badge"
                  data-testid="dash-plan-badge"
                  data-plan={String(planLabel).toLowerCase().replace(/\s+/g, "-")}
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    padding: "3px 8px",
                    borderRadius: 999,
                    ...planStyle,
                  }}
                >
                  {planLabel}
                </span>
              );
            })()}
            <span style={{ fontSize: 10, color: C.muted }}>Version: {APP_VERSION}</span>
          </div>

        </div>
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 8 }}>
          <ProfileAvatar
            url={profilePhotoUrl}
            name={profileDisplayName}
            size={30}
            onClick={() => setShowProfilePhoto(true)}
            title="View profile"
          />
          <button
            onClick={() => setShowGuide(true)}
            type="button"
            aria-label="Open app guide"
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: C.cardBg,
              border: `1px solid ${C.seafoam}66`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: C.white,
              padding: 0,
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            ?
          </button>
          <button
            onClick={() => setShowSettings(true)}
            type="button"
            aria-label="Open settings"
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: C.cardBg,
              border: `1px solid ${C.seafoam}66`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: C.white,
              padding: 0,
              lineHeight: 0,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.9" />
              <path
                d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33 1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82 1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {showSettings && (
            <div
              style={{
                position: "absolute",
                top: 44,
                right: 0,
                width: 220,
                background: C.settingsBg,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                padding: 12,
                boxShadow: C.menuShadow,
                zIndex: 50,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: C.white, fontWeight: 700 }}>Preferences</div>
                <button
                  onClick={() => setShowSettings(false)}
                  type="button"
                  style={{
                    background: "transparent",
                    border: "none",
                    color: C.muted,
                    cursor: "pointer",
                    fontSize: 14,
                  }}
                >
                  ✕
                </button>
              </div>
              <div style={{ marginBottom: 8 }}>
                <CollapsibleSection
                  open={showUserPreferences}
                  onToggle={() => setShowUserPreferences((v) => !v)}
                  headerTitle={showUserPreferences ? "Hide user preferences" : "Show user preferences"}
                  bodyStyle={{ padding: "0 12px 10px" }}
                  title={<span style={{ fontSize: 11, fontWeight: 700, color: C.white }}>User Preferences</span>}
                >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "10px 0" }}>
                      <div style={{ flex: 1, minWidth: 0, paddingRight: 4 }}>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Profile Photo</div>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.2 }}>
                          A small photo shown on your dashboard.
                        </div>
                        {photoError && (
                          <div style={{ fontSize: 10, color: C.warning, lineHeight: 1.3, marginTop: 4 }}>{photoError}</div>
                        )}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                        <ProfileAvatar url={profilePhotoUrl} name={profileDisplayName} size={40} title="Your profile photo" />
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <button
                            type="button"
                            onClick={() => profilePhotoInputRef.current && profilePhotoInputRef.current.click()}
                            disabled={photoUploading}
                            style={{
                              whiteSpace: "nowrap",
                              padding: "6px 10px",
                              borderRadius: 10,
                              border: `1px solid ${C.seafoam}66`,
                              background: `${C.accent}2E`,
                              color: C.white,
                              cursor: photoUploading ? "default" : "pointer",
                              fontSize: 10,
                              fontWeight: 700,
                            }}
                          >
                            {photoUploading ? "Saving…" : profilePhotoUrl ? "Change" : "Add photo"}
                          </button>
                          {profilePhotoUrl && !photoUploading && (
                            <button
                              type="button"
                              onClick={handleProfilePhotoRemove}
                              style={{
                                whiteSpace: "nowrap",
                                padding: "4px 10px",
                                borderRadius: 10,
                                border: `1px solid ${C.border}`,
                                background: "transparent",
                                color: C.muted,
                                cursor: "pointer",
                                fontSize: 10,
                                fontWeight: 600,
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <input
                          ref={profilePhotoInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleProfilePhotoPick}
                          style={{ display: "none" }}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                      <div style={{ flex: 1, minWidth: 0, paddingRight: 4 }}>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Theme</div>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.2 }}>
                          Use Light mode for bright, sunny conditions.
                        </div>
                      </div>
                      <div style={{ display: "inline-flex", border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={() => onThemeModeChange && onThemeModeChange("dark")}
                          style={{
                            minWidth: 50,
                            whiteSpace: "nowrap",
                            padding: "6px 10px",
                            border: "none",
                            background: themeMode === "dark" ? `${C.accent}2E` : "transparent",
                            color: themeMode === "dark" ? C.white : C.muted,
                            cursor: "pointer",
                            fontSize: 10,
                            fontWeight: themeMode === "dark" ? 700 : 600,
                          }}
                        >
                          Dark
                        </button>
                        <button
                          type="button"
                          onClick={() => onThemeModeChange && onThemeModeChange("light")}
                          style={{
                            minWidth: 50,
                            whiteSpace: "nowrap",
                            padding: "6px 10px",
                            border: "none",
                            borderLeft: `1px solid ${C.border}`,
                            background: themeMode === "light" ? `${C.accent}2E` : "transparent",
                            color: themeMode === "light" ? C.white : C.muted,
                            cursor: "pointer",
                            fontSize: 10,
                            fontWeight: themeMode === "light" ? 700 : 600,
                          }}
                        >
                          Light
                        </button>
                      </div>
                    </div>

                    {userProfile?.role === "fisherman" && (
                    <div style={{ padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Where You Fish</div>
                      <div style={{ fontSize: 10, color: C.white, lineHeight: 1.3, marginBottom: 8 }}>
                        Update your water types anytime. This drives your Conditions, Prep Score, and species/lure lists. Saltwater covers Inshore / Offshore; Freshwater covers Lake / River.
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {WATER_TYPE_OPTIONS.map((opt) => {
                          const selected = enrolledWaterTypes.includes(opt.id);
                          return (
                            <button
                              key={opt.id}
                              type="button"
                              data-testid={`pref-watertype-${opt.id}`}
                              disabled={waterTypeSaving}
                              onClick={() => toggleWaterTypePref(opt.id)}
                              style={{
                                padding: "6px 11px",
                                borderRadius: 999,
                                border: `1px solid ${selected ? C.seafoam : C.border}`,
                                background: selected ? `${C.accent}2E` : "transparent",
                                color: selected ? C.white : C.muted,
                                fontSize: 10,
                                fontWeight: selected ? 700 : 600,
                                cursor: waterTypeSaving ? "default" : "pointer",
                                opacity: waterTypeSaving ? 0.6 : 1,
                              }}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      {enrolledWaterTypes.map((wt) => (
                        <div key={wt} style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>
                            {wt === "saltwater"
                              ? "Saltwater — Inshore, Offshore, or both"
                              : "Freshwater — Lake, River, or both"}
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {(SUBTYPE_OPTIONS[wt] || []).map((opt) => {
                              const selected = enrolledSubTypes.includes(opt.id);
                              return (
                                <button
                                  key={opt.id}
                                  type="button"
                                  data-testid={`pref-subtype-${opt.id}`}
                                  disabled={waterTypeSaving}
                                  onClick={() => toggleSubTypePref(opt.id)}
                                  style={{
                                    padding: "6px 11px",
                                    borderRadius: 999,
                                    border: `1px solid ${selected ? C.seafoam : C.border}`,
                                    background: selected ? `${C.accent}2E` : "transparent",
                                    color: selected ? C.white : C.muted,
                                    fontSize: 10,
                                    fontWeight: selected ? 700 : 600,
                                    cursor: waterTypeSaving ? "default" : "pointer",
                                    opacity: waterTypeSaving ? 0.6 : 1,
                                  }}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      {enrolledWaterTypes.length === 0 && (
                        <div style={{ fontSize: 10, color: C.warning, marginTop: 6 }}>
                          Select at least one water type.
                        </div>
                      )}
                      {waterTypeError && (
                        <div style={{ fontSize: 10, color: C.warning, marginTop: 6 }}>{waterTypeError}</div>
                      )}
                    </div>
                    )}

                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                      <div style={{ flex: 1, minWidth: 0, paddingRight: 4 }}>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Text Size</div>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.2 }}>
                          Slightly increase text size across the app.
                        </div>
                      </div>
                      <div style={{ display: "inline-flex", border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={() => onFontSizeModeChange && onFontSizeModeChange("normal")}
                          style={{
                            minWidth: 58,
                            whiteSpace: "nowrap",
                            padding: "6px 10px",
                            border: "none",
                            background: fontSizeMode !== "large" ? "rgba(46, 191, 165, 0.18)" : "transparent",
                            color: fontSizeMode !== "large" ? C.white : C.muted,
                            cursor: "pointer",
                            fontSize: 10,
                            fontWeight: fontSizeMode !== "large" ? 700 : 600,
                          }}
                        >
                          Normal
                        </button>
                        <button
                          type="button"
                          onClick={() => onFontSizeModeChange && onFontSizeModeChange("large")}
                          style={{
                            minWidth: 58,
                            whiteSpace: "nowrap",
                            padding: "6px 10px",
                            border: "none",
                            borderLeft: `1px solid ${C.border}`,
                            background: fontSizeMode === "large" ? "rgba(46, 191, 165, 0.18)" : "transparent",
                            color: fontSizeMode === "large" ? C.white : C.muted,
                            cursor: "pointer",
                            fontSize: 10,
                            fontWeight: fontSizeMode === "large" ? 700 : 600,
                          }}
                        >
                          Larger
                        </button>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                      <div>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>GoPro Integration</div>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.2 }}>
                          Show the GoPro button on the trip dashboard for connecting and offloading clips.
                        </div>
                      </div>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={!!showGoProButton}
                          onChange={(e) => setShowGoProButton && setShowGoProButton(e.target.checked)}
                          style={{ width: 14, height: 14 }}
                        />
                        <span style={{ fontSize: 10, color: C.white }}>{showGoProButton ? "On" : "Off"}</span>
                      </label>
                    </div>

                    {showGoProButton && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                        <div>
                          <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>GoPro Clip Limit</div>
                          <div style={{ fontSize: 10, color: C.white, lineHeight: 1.2 }}>
                            Auto-stop a GoPro recording at this length (with a 30-second warning). Max 10 minutes.
                          </div>
                        </div>
                        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                          <select
                            value={Math.min(10, Math.max(1, Number(goProMaxClipMins) || 5))}
                            onChange={(e) => setGoProMaxClipMins && setGoProMaxClipMins(Number(e.target.value))}
                            style={{
                              background: C.inputBg || "rgba(255,255,255,0.06)",
                              color: C.white,
                              border: `1px solid ${C.border}`,
                              borderRadius: 6,
                              padding: "4px 6px",
                              fontSize: 11,
                            }}
                          >
                            {Array.from({ length: 10 }, (_, i) => i + 1).map((m) => (
                              <option key={m} value={m}>{m} min</option>
                            ))}
                          </select>
                        </label>
                      </div>
                    )}

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                      <div>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Sound Alerts</div>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.2 }}>
                          Play a tone for tournament start/end reminders and boundary breaches.
                        </div>
                      </div>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={!!soundAlerts}
                          onChange={(e) => setSoundAlerts && setSoundAlerts(e.target.checked)}
                          style={{ width: 14, height: 14 }}
                        />
                        <span style={{ fontSize: 10, color: C.white }}>{soundAlerts ? "On" : "Off"}</span>
                      </label>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                      <div>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Vibration Alerts</div>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.2 }}>
                          Vibrate the device for tournament start/end reminders and boundary breaches (Android PWA).
                        </div>
                      </div>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={!!vibrationAlerts}
                          onChange={(e) => setVibrationAlerts && setVibrationAlerts(e.target.checked)}
                          style={{ width: 14, height: 14 }}
                        />
                        <span style={{ fontSize: 10, color: C.white }}>{vibrationAlerts ? "On" : "Off"}</span>
                      </label>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                      <div>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Smart Trip Suggestions</div>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.2 }}>
                          Suggest the best time to fish (dawn/dusk + solunar) when scheduling a trip.
                        </div>
                      </div>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={!!tripSmartSuggest}
                          onChange={(e) => toggleTripSmartSuggest(e.target.checked)}
                          style={{ width: 14, height: 14 }}
                        />
                        <span style={{ fontSize: 10, color: C.white }}>{tripSmartSuggest ? "On" : "Off"}</span>
                      </label>
                    </div>

                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                      <div style={{ flex: 1, minWidth: 0, paddingRight: 4 }}>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Video Sharing</div>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.3 }}>
                          Share your catch videos on the dashboard, and enable posting individual certified clips publicly to THE 99's YouTube channel. Each YouTube post is confirmed by you after a clip is certified.
                        </div>
                      </div>
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", flexShrink: 0 }}>
                        <input
                          type="checkbox"
                          data-testid="pref-share-videos"
                          checked={shareVideos}
                          onChange={(e) => toggleShareVideos(e.target.checked)}
                          style={{ width: 14, height: 14 }}
                        />
                        <span style={{ fontSize: 10, color: C.white }}>{shareVideos ? "On" : "Off"}</span>
                      </label>
                    </div>

                    <div style={{ padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
                        <div style={{ fontSize: 11, color: C.muted }}>My Species</div>
                        <div style={{ fontSize: 9, color: C.muted }}>{preferredSpeciesIds.length} / {maxSpecies}</div>
                      </div>
                      <div style={{ fontSize: 10, color: C.white, lineHeight: 1.3, marginBottom: 8 }}>
                        The {activeWaterLabel ? `${activeWaterLabel} ` : ""}species you target. The Catch Log shows these by default (tournament rules override this list).
                      </div>
                      {speciesLoading || !preferredSpeciesLoaded ? (
                        <div style={{ fontSize: 10, color: C.muted }}>Loading…</div>
                      ) : speciesCatalog.length === 0 ? (
                        <div style={{ fontSize: 10, color: C.muted }}>No species catalog available.</div>
                      ) : (
                        <>
                          {visibleSelectedSpecies.length > 0 ? (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                              {visibleSelectedSpecies.map((sp) => (
                                <span
                                  key={sp.id}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "5px 8px",
                                    borderRadius: 999,
                                    border: `1px solid ${C.accent}`,
                                    background: `${C.accent}2E`,
                                    color: C.white,
                                    fontSize: 10,
                                    fontWeight: 700,
                                  }}
                                >
                                  {sp.label}
                                  <button
                                    type="button"
                                    aria-label={`Remove ${sp.label}`}
                                    onClick={() => removePreferredSpecies(sp.id)}
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      color: C.white,
                                      cursor: "pointer",
                                      fontSize: 13,
                                      lineHeight: 1,
                                      padding: 0,
                                    }}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div style={{ fontSize: 10, color: C.muted, marginBottom: 8 }}>
                              No {activeWaterLabel ? `${activeWaterLabel} ` : ""}species picked yet — search below to add.
                            </div>
                          )}

                          {speciesAtLimit && (
                            <div style={{ fontSize: 10, color: C.warning, marginTop: 2, marginBottom: 6 }}>
                              You're at your plan's limit of {maxSpecies} species{isProActive(userProfile) ? "" : " — upgrade to Pro to track more"}. Remove one to add a different one.
                            </div>
                          )}
                          <input
                            type="text"
                            value={speciesQuery}
                            onChange={(e) => setSpeciesQuery(e.target.value)}
                            placeholder={speciesAtLimit ? "Remove a species to add another…" : `Search ${activeWaterLabel ? `${activeWaterLabel} ` : ""}fish…`}
                            disabled={speciesAtLimit}
                            data-testid="pref-species-search"
                            style={{
                              width: "100%",
                              height: 30,
                              borderRadius: 8,
                              border: `1px solid ${C.border}`,
                              background: C.cardBg,
                              color: speciesAtLimit ? C.muted : C.white,
                              padding: "0 8px",
                              fontSize: 11,
                              boxSizing: "border-box",
                              opacity: speciesAtLimit ? 0.6 : 1,
                            }}
                          />
                          {!speciesAtLimit && speciesSearchResults.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                              {speciesSearchResults.map((sp) => (
                                <button
                                  key={sp.id}
                                  type="button"
                                  onClick={() => addPreferredSpecies(sp.id)}
                                  style={{
                                    padding: "5px 9px",
                                    borderRadius: 999,
                                    border: `1px solid ${C.border}`,
                                    background: "transparent",
                                    color: C.muted,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                  }}
                                >
                                  + {sp.label}
                                </button>
                              ))}
                            </div>
                          )}
                          {!speciesAtLimit && speciesQuery.trim() && speciesSearchResults.length === 0 && (
                            <div style={{ fontSize: 10, color: C.muted, marginTop: 8 }}>No matching species.</div>
                          )}
                          {otherWaterSelectedCount > 0 && (
                            <div style={{ fontSize: 9, color: C.muted, marginTop: 6 }}>
                              {otherWaterSelectedCount} more selected for your other water type.
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                      <div style={{ flex: 1, minWidth: 0, paddingRight: 4 }}>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Rate the99</div>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.3 }}>
                          Enjoying the app? Leave a rating on the {isAndroidDevice ? "Play Store" : "App Store"} — it really helps us out.
                        </div>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={openStoreListing}
                        style={{ fontSize: 10, flexShrink: 0, whiteSpace: "nowrap" }}
                      >
                        Rate the99
                      </Button>
                    </div>

                    {!IS_NATIVE_APP && (
                      <div style={{ padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Install App</div>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.3, marginBottom: 8 }}>
                          Add The 99 to your home screen for a full-screen mobile experience.
                        </div>
                        {isStandaloneInstalled && (
                          <div style={{ fontSize: 10, color: C.accent, fontWeight: 700 }}>Installed on this device</div>
                        )}
                        {!isStandaloneInstalled && isAndroidDevice && (
                          <Button
                            variant="secondary"
                            size="sm"
                            fullWidth
                            onClick={handleInstallApp}
                            style={{ fontSize: 10 }}
                          >
                            {canPromptInstall ? "Add to Home Screen" : "Install App"}
                          </Button>
                        )}
                        {!isStandaloneInstalled && !isAndroidDevice && !showIosInstallHint && (
                          <div style={{ fontSize: 9, color: C.muted }}>
                            Install prompt appears after using the app for a bit in Chrome.
                          </div>
                        )}
                        {!isStandaloneInstalled && showIosInstallHint && (
                          <div style={{ fontSize: 9, color: C.muted, lineHeight: 1.35 }}>
                            On iPhone/iPad: open Safari Share menu, then tap Add to Home Screen.
                          </div>
                        )}
                      </div>
                    )}
                </CollapsibleSection>
              </div>

              <div style={{ marginBottom: 8 }}>
                <CollapsibleSection
                  open={showConditionsPreferences}
                  onToggle={() => setShowConditionsPreferences((v) => !v)}
                  headerTitle={showConditionsPreferences ? "Hide conditions preferences" : "Show conditions preferences"}
                  bodyStyle={{ padding: "0 12px 10px" }}
                  title={<span style={{ fontSize: 11, fontWeight: 700, color: C.white }}>Conditions Preferences</span>}
                >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                      <div>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>{freshwaterActive ? "USGS Gage" : "Station"}</div>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.2 }}>
                          {freshwaterActive
                            ? "Choose a USGS gage for flow/level & water temp."
                            : "Choose a station for tide predictions."}
                        </div>
                      </div>
                      <button
                        onClick={() => { if (freshwaterActive) { setShowGagePicker((s) => !s); } else { setShowStationPicker((s) => !s); } }}
                        type="button"
                        style={{
                          padding: "6px 10px",
                          borderRadius: 10,
                          border: `1px solid ${C.border}`,
                          background: "transparent",
                          color: C.white,
                          cursor: "pointer",
                          fontSize: 10,
                        }}
                      >
                        {freshwaterActive
                          ? (showGagePicker ? "Hide" : "Select")
                          : (showStationPicker ? "Hide" : "Select")}
                      </button>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${C.border}` }}>
                      <div>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>Temperature Unit</div>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.2 }}>
                          Choose how water temperature is displayed.
                        </div>
                      </div>
                      <div style={{ display: "inline-flex", border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
                        <button
                          type="button"
                          onClick={() => setTempUnit("F")}
                          style={{
                            padding: "6px 10px",
                            border: "none",
                            background: tempUnit === "F" ? "rgba(46, 191, 165, 0.18)" : "transparent",
                            color: C.white,
                            cursor: "pointer",
                            fontSize: 10,
                          }}
                        >
                          °F
                        </button>
                        <button
                          type="button"
                          onClick={() => setTempUnit("C")}
                          style={{
                            padding: "6px 10px",
                            border: "none",
                            borderLeft: `1px solid ${C.border}`,
                            background: tempUnit === "C" ? "rgba(46, 191, 165, 0.18)" : "transparent",
                            color: C.white,
                            cursor: "pointer",
                            fontSize: 10,
                          }}
                        >
                          °C
                        </button>
                      </div>
                    </div>

                    {showStationPicker && !tideInactive && (
                      <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                        {stations.length === 0 ? (
                          <div style={{ fontSize: 10, color: tideError ? C.red : C.muted }}>
                            {tideError ? `Error: ${tideError}` : "Finding nearby stations…"}
                          </div>
                        ) : (
                          stations.slice(0, 8).map((st) => (
                            <button
                              key={st.id}
                              type="button"
                              onClick={() => {
                                setSelectedStationId(st.id);
                                setShowStationPicker(false);
                              }}
                              style={{
                                width: "100%",
                                textAlign: "left",
                                padding: "8px 10px",
                                borderRadius: 10,
                                border: `1px solid ${selectedStationId === st.id ? C.seafoam : C.border}`,
                                background: selectedStationId === st.id ? "rgba(46, 191, 165, 0.12)" : "transparent",
                                color: C.white,
                                cursor: "pointer",
                                marginBottom: 6,
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span style={{ fontSize: 11, fontWeight: 700 }}>{st.name}</span>
                                <span style={{ fontSize: 10, color: C.muted }}>{st.geoDist?.toFixed(1)} mi</span>
                              </div>
                              <div style={{ fontSize: 9, color: C.muted }}>{st.noaaDist ? `${st.noaaDist.toFixed(1)} mi NOAA` : ""}</div>
                            </button>
                          ))
                        )}
                      </div>
                    )}

                    {showGagePicker && freshwaterActive && (
                      <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                        {(!flow.sites || flow.sites.length === 0) ? (
                          <div style={{ fontSize: 10, color: flow.error ? C.red : C.muted }}>
                            {flow.error ? `Error: ${flow.error}` : "Finding nearby USGS gages…"}
                          </div>
                        ) : (
                          flow.sites.slice(0, 10).map((st) => (
                            <button
                              key={st.id}
                              type="button"
                              onClick={() => {
                                setSelectedGageId(st.id);
                                setShowGagePicker(false);
                              }}
                              style={{
                                width: "100%",
                                textAlign: "left",
                                padding: "8px 10px",
                                borderRadius: 10,
                                border: `1px solid ${(selectedGageId || flow.siteId) === st.id ? C.seafoam : C.border}`,
                                background: (selectedGageId || flow.siteId) === st.id ? "rgba(46, 191, 165, 0.12)" : "transparent",
                                color: C.white,
                                cursor: "pointer",
                                marginBottom: 6,
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                <span style={{ fontSize: 11, fontWeight: 700 }}>{st.name}</span>
                                <span style={{ fontSize: 10, color: C.muted, flexShrink: 0 }}>{st.dist?.toFixed(1)} mi</span>
                              </div>
                              <div style={{ fontSize: 9, color: C.muted }}>
                                {[st.hasFlow ? "flow" : null, st.hasStage ? "stage" : null, st.hasElev ? "level" : null].filter(Boolean).join(" · ") || "USGS gage"}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                </CollapsibleSection>
              </div>

              {!isTournamentArchived && (
              <div style={{ marginBottom: 8 }}>
                <CollapsibleSection
                  open={showTournamentConfiguration}
                  onToggle={() => setShowTournamentConfiguration((v) => !v)}
                  headerTitle={showTournamentConfiguration ? "Hide tournament configuration" : "Show tournament configuration"}
                  bodyStyle={{ padding: "0 12px 10px" }}
                  title={<span style={{ fontSize: 11, fontWeight: 700, color: C.white }}>Tournament Configuration</span>}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Tournament</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.2 }}>
                          {fishermanTournamentName || "Not assigned"}
                        </div>
                        {tournamentAreaRestricted && (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <span
                              style={{
                                fontSize: 8,
                                letterSpacing: 0.6,
                                textTransform: "uppercase",
                                color: C.warning,
                                border: `1px solid ${C.warning}66`,
                                padding: "2px 6px",
                                borderRadius: 999,
                                background: `${C.warning}1a`,
                                fontWeight: 700,
                              }}
                            >
                              Area Restriction
                            </span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setShowAreaRestrictionInfo(true); }}
                              style={{
                                background: "none", border: `1px solid ${C.border}`, borderRadius: 99,
                                width: 14, height: 14, padding: 0, cursor: "pointer",
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0,
                              }}
                              title="What is an area restriction?"
                            >
                              <span style={{ fontSize: 8, color: C.muted, lineHeight: 1, fontWeight: 700 }}>i</span>
                            </button>
                          </span>
                        )}
                        {!tournamentAreaRestricted && fishermanTournamentName && (
                          <span
                            style={{
                              fontSize: 8,
                              letterSpacing: 0.6,
                              textTransform: "uppercase",
                              color: C.muted,
                              border: `1px solid ${C.border}`,
                              padding: "2px 6px",
                              borderRadius: 999,
                              background: "transparent",
                              fontWeight: 700,
                            }}
                          >
                            No Boundary
                          </span>
                        )}
                      </div>
                    </div>

                    {!!fishermanTournamentName && tournamentDatesDefined && (
                      <div
                        style={{
                          borderRadius: 8,
                          background: tournamentDateMismatch ? `${C.warning}14` : `${C.seafoam}14`,
                          border: `1px solid ${tournamentDateMismatch ? C.warning + "44" : C.seafoam + "44"}`,
                          padding: "7px 10px",
                          display: "flex",
                          flexDirection: "column",
                          gap: 3,
                        }}
                      >
                        {tournamentDateRangeLabel && (
                          <span style={{ fontSize: 10, color: C.muted }}>
                            {tournamentDateRangeLabel}
                          </span>
                        )}
                        {tournamentDateMismatch ? (
                          <span style={{ fontSize: 10, color: C.warning, fontWeight: 700 }}>
                            {tournamentNotYetStarted
                              ? "Tournament has not started yet. Restrictions are not active."
                              : "Tournament has ended. Restrictions are no longer active."}
                          </span>
                        ) : (
                          <span style={{ fontSize: 10, color: C.seafoam, fontWeight: 700 }}>
                            Tournament is currently active.
                          </span>
                        )}
                      </div>
                    )}

                    {!!fishermanTeamName && (
                      <div>
                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Team</div>
                        <div style={{ fontSize: 10, color: C.white, lineHeight: 1.2 }}>{fishermanTeamName}</div>
                      </div>
                    )}

                    {!!fishermanTournamentName && (
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={!!leavingTournament}
                          onClick={() => {
                            setShowSettings(false);
                            onLeaveTournament && onLeaveTournament();
                          }}
                          style={{ fontSize: 10 }}
                        >
                          {leavingTournament ? "Leaving..." : "Leave"}
                        </Button>
                      </div>
                    )}
                  </div>
                </CollapsibleSection>
              </div>
              )}

              {/* Account Settings card */}
              <div style={{ marginBottom: 8 }}>
                <CollapsibleSection
                  open={showAccountSettings}
                  onToggle={() => { setShowAccountSettings((v) => !v); if (showAccountSettings) { setAccountSection(null); setAcctError(""); setAcctSuccess(""); } }}
                  headerTitle={showAccountSettings ? "Hide account settings" : "Show account settings"}
                  bodyStyle={{ padding: "0 12px 10px" }}
                  title={<span style={{ fontSize: 11, fontWeight: 700, color: C.white }}>Account Settings</span>}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

                    {/* Current email display */}
                    <div style={{ fontSize: 10, color: C.muted }}>
                      Signed in as <span style={{ color: C.white, fontWeight: 700 }}>{acctEmailUser || "—"}</span>
                    </div>

                    {!acctIsPasswordUser && (
                      <div style={{ fontSize: 10, color: C.muted, fontStyle: "italic", lineHeight: 1.4 }}>
                        You signed in with {acctLoginProvider === "google.com" ? "Google" : acctLoginProvider === "facebook.com" ? "Facebook" : "a social provider"}. Password and email changes are not available for social sign-in accounts.
                      </div>
                    )}

                    {/* Change Password */}
                    {acctIsPasswordUser && (
                      <CollapsibleSection
                        open={accountSection === "password"}
                        onToggle={() => handleAccountSectionOpen(accountSection === "password" ? null : "password")}
                        headerTitle={accountSection === "password" ? "Hide change password" : "Show change password"}
                        bodyStyle={{ padding: 0 }}
                        title={<span style={{ fontSize: 11, fontWeight: 700, color: C.white }}>Change Password</span>}
                      >
                          <form onSubmit={handlePasswordChange} style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 10, borderTop: `1px solid ${C.border}` }}>
                            <div style={{ paddingTop: 10 }}>
                              {/* Current password */}
                              <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>Current password</div>
                              <div style={{ position: "relative" }}>
                                <input
                                  type={acctShowCurrentPw ? "text" : "password"}
                                  value={acctCurrentPassword}
                                  onChange={(e) => setAcctCurrentPassword(e.target.value)}
                                  placeholder="Current password"
                                  autoComplete="current-password"
                                  disabled={acctLoading}
                                  style={{ width: "100%", boxSizing: "border-box", padding: "9px 38px 9px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.cardBg, color: C.white, outline: "none", fontSize: 11 }}
                                />
                                <button type="button" onClick={() => setAcctShowCurrentPw((v) => !v)} aria-label={acctShowCurrentPw ? "Hide" : "Show"}
                                  style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.muted, cursor: "pointer", padding: 2, display: "flex", alignItems: "center" }}>
                                  {acctShowCurrentPw
                                    ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                    : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                                </button>
                              </div>
                            </div>

                            {/* New password */}
                            <div>
                              <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>New password</div>
                              <div style={{ position: "relative" }}>
                                <input
                                  type={acctShowNewPw ? "text" : "password"}
                                  value={acctNewPassword}
                                  onChange={(e) => setAcctNewPassword(e.target.value)}
                                  placeholder="New password"
                                  autoComplete="new-password"
                                  disabled={acctLoading}
                                  style={{ width: "100%", boxSizing: "border-box", padding: "9px 38px 9px 10px", borderRadius: 8, border: `1px solid ${acctNewPassword && !acctNewPwValid ? C.warning : C.border}`, background: C.cardBg, color: C.white, outline: "none", fontSize: 11 }}
                                />
                                <button type="button" onClick={() => setAcctShowNewPw((v) => !v)} aria-label={acctShowNewPw ? "Hide" : "Show"}
                                  style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.muted, cursor: "pointer", padding: 2, display: "flex", alignItems: "center" }}>
                                  {acctShowNewPw
                                    ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                    : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                                </button>
                              </div>
                            </div>

                            {/* Requirements checklist */}
                            {acctNewPassword.length > 0 && (
                              <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, background: "rgba(6,10,16,0.55)", padding: "8px 10px", display: "flex", flexDirection: "column", gap: 5 }}>
                                {acctPwRequirements.map(({ label, pass }) => (
                                  <div key={label} style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 10, color: pass ? C.seafoam : C.muted, transition: "color 0.15s" }}>
                                    <span style={{ width: 12, fontWeight: 700 }}>{pass ? "✓" : "○"}</span>
                                    <span>{label}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {acctError && accountSection === "password" && (
                              <div style={{ fontSize: 10, color: C.warning, lineHeight: 1.4 }}>{acctError}</div>
                            )}
                            {acctSuccess && accountSection === "password" && (
                              <div style={{ fontSize: 10, color: C.seafoam, fontWeight: 700, lineHeight: 1.4 }}>{acctSuccess}</div>
                            )}

                            <div style={{ display: "flex", gap: 8 }}>
                              <Button type="submit" variant="primary" size="sm" disabled={acctLoading || !acctNewPwValid || !acctCurrentPassword}
                                style={{ flex: 1, fontSize: 10 }}>
                                {acctLoading ? "Updating..." : "Update Password"}
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleAccountSectionOpen(null)}
                                style={{ flex: 1, fontSize: 10 }}>
                                Cancel
                              </Button>
                            </div>
                          </form>
                      </CollapsibleSection>
                    )}

                    {/* Change Email */}
                    {acctIsPasswordUser && (
                      <CollapsibleSection
                        open={accountSection === "email"}
                        onToggle={() => handleAccountSectionOpen(accountSection === "email" ? null : "email")}
                        headerTitle={accountSection === "email" ? "Hide change email" : "Show change email"}
                        bodyStyle={{ padding: 0 }}
                        title={<span style={{ fontSize: 11, fontWeight: 700, color: C.white }}>Change Email</span>}
                      >
                          <form onSubmit={handleEmailChange} style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 10, borderTop: `1px solid ${C.border}` }}>
                            <div style={{ paddingTop: 10 }}>
                              <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>New email address</div>
                              <input
                                type="email"
                                value={acctNewEmail}
                                onChange={(e) => setAcctNewEmail(e.target.value)}
                                placeholder="new@example.com"
                                autoComplete="email"
                                disabled={acctLoading}
                                style={{ width: "100%", boxSizing: "border-box", padding: "9px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.cardBg, color: C.white, outline: "none", fontSize: 11 }}
                              />
                            </div>

                            <div>
                              <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>Current password (required)</div>
                              <div style={{ position: "relative" }}>
                                <input
                                  type={acctShowEmailPw ? "text" : "password"}
                                  value={acctEmailCurrentPassword}
                                  onChange={(e) => setAcctEmailCurrentPassword(e.target.value)}
                                  placeholder="Current password"
                                  autoComplete="current-password"
                                  disabled={acctLoading}
                                  style={{ width: "100%", boxSizing: "border-box", padding: "9px 38px 9px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.cardBg, color: C.white, outline: "none", fontSize: 11 }}
                                />
                                <button type="button" onClick={() => setAcctShowEmailPw((v) => !v)} aria-label={acctShowEmailPw ? "Hide" : "Show"}
                                  style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.muted, cursor: "pointer", padding: 2, display: "flex", alignItems: "center" }}>
                                  {acctShowEmailPw
                                    ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                                    : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                                </button>
                              </div>
                            </div>

                            <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.45 }}>
                              A verification link will be sent to your new email. Your email will not change until you click the link.
                            </div>

                            {acctError && accountSection === "email" && (
                              <div style={{ fontSize: 10, color: C.warning, lineHeight: 1.4 }}>{acctError}</div>
                            )}
                            {acctSuccess && accountSection === "email" && (
                              <div style={{ fontSize: 10, color: C.seafoam, fontWeight: 700, lineHeight: 1.4 }}>{acctSuccess}</div>
                            )}

                            <div style={{ display: "flex", gap: 8 }}>
                              <Button type="submit" variant="primary" size="sm" disabled={acctLoading || !acctNewEmail.trim() || !acctEmailCurrentPassword}
                                style={{ flex: 1, fontSize: 10 }}>
                                {acctLoading ? "Sending..." : "Send Verification"}
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleAccountSectionOpen(null)}
                                style={{ flex: 1, fontSize: 10 }}>
                                Cancel
                              </Button>
                            </div>
                          </form>
                      </CollapsibleSection>
                    )}

                    {/* Sign Out */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 4 }}>
                      <div style={{ fontSize: 10, color: C.muted }}>Sign out of the app.</div>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => { setShowSettings(false); onSignOut && onSignOut(); }}
                        style={{ fontSize: 10 }}
                      >
                        Sign Out
                      </Button>
                    </div>

                  </div>
                </CollapsibleSection>
              </div>

            </div>
          )}
        </div>
      </div>
      {(() => {
        // Renewal / expiry notification banner. Three orthogonal triggers,
        // one element. See docs/subscription-matrix.md "Mechanism 3".
        //   - past_due: payment failed; user is still in grace and can fix
        //   - cancelAtPeriodEnd: user opted out; will downgrade on periodEnd
        //   - daysLeft <= 7 while still active: heads-up before auto-renew
        // No banner once status flips to canceled/expired — badge already
        // shows Free, banner would be redundant noise.
        const view = getSubscriptionView(userProfile);
        let variant = null;
        let copy = null;
        if (view.status === "past_due") {
          variant = "past-due";
          // No steering to external payment (store IAP policy): status only.
          copy = "Pro payment issue — Pro access may pause soon.";
        } else if (view.isProActive && view.cancelAtPeriodEnd && view.daysLeft != null) {
          variant = "ends-soon";
          // No steering to external payment (store IAP policy): status only.
          copy = `Pro ends in ${view.daysLeft} day${view.daysLeft === 1 ? "" : "s"}.`;
        } else if (view.isProActive && view.daysLeft != null && view.daysLeft <= 7) {
          variant = "renews-soon";
          copy = `Pro renews in ${view.daysLeft} day${view.daysLeft === 1 ? "" : "s"}.`;
        }
        if (!variant) return null;
        const isWarn = variant === "past-due";
        const accent = isWarn ? C.warning : C.seafoam;
        return (
          <div
            id="dash-renewal-banner"
            data-testid="dash-renewal-banner"
            data-banner-variant={variant}
            role="status"
            style={{
              margin: "0 14px 10px",
              padding: "8px 12px",
              borderRadius: 8,
              border: `1px solid ${accent}66`,
              background: `${accent}1a`,
              color: C.white,
              fontSize: 12,
              lineHeight: 1.35,
            }}
          >
            <span style={{ fontWeight: 700, color: accent, letterSpacing: 1, fontSize: 10, textTransform: "uppercase", marginRight: 8 }}>
              {isWarn ? "Action needed" : "Heads up"}
            </span>
            <span
              id="dash-renewal-banner-copy"
              data-testid="dash-renewal-banner-copy"
            >
              {copy}
            </span>
          </div>
        );
      })()}
      {hasUpcomingTournament && prefishAnswer !== "no" && (
        <div
          data-testid="dash-prefish-banner"
          role="status"
          style={{
            margin: "0 14px 10px",
            padding: "10px 12px",
            borderRadius: 10,
            border: `1px solid ${C.seafoam}66`,
            background: `${C.seafoam}14`,
          }}
        >
          <div style={{ fontWeight: 700, color: C.seafoam, letterSpacing: 1, fontSize: 10, textTransform: "uppercase", marginBottom: 4 }}>
            Upcoming tournament
          </div>
          {prefishAnswer === "yes" ? (
            <>
              <div style={{ fontSize: 11, color: C.white, lineHeight: 1.4 }}>
                Prefishing mode on for <strong>{fishermanTournamentName}</strong>. Trips you start today are tagged as prefishing so we can build your tournament strategy from them.
              </div>
              <button
                type="button"
                data-testid="dash-prefish-off"
                onClick={() => answerPrefish("no")}
                style={{
                  marginTop: 8,
                  background: "transparent",
                  border: `1px solid ${C.border}`,
                  color: C.muted,
                  borderRadius: 8,
                  padding: "5px 10px",
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Turn off for today
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 11, color: C.white, lineHeight: 1.4 }}>
                I see you have <strong>{fishermanTournamentName}</strong>{" "}
                {daysUntilTournament <= 0
                  ? "starting today"
                  : daysUntilTournament === 1
                  ? "tomorrow"
                  : `in ${daysUntilTournament} days`}
                . Are you prefishing today? If so, I'll tag today's trips and help build a tournament strategy from your prefishing (and any additional days).
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  data-testid="dash-prefish-yes"
                  onClick={() => answerPrefish("yes")}
                  style={{
                    background: `${C.seafoam}2E`,
                    border: `1px solid ${C.seafoam}`,
                    color: C.white,
                    borderRadius: 8,
                    padding: "6px 12px",
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Yes, prefishing
                </button>
                <button
                  type="button"
                  data-testid="dash-prefish-no"
                  onClick={() => answerPrefish("no")}
                  style={{
                    background: "transparent",
                    border: `1px solid ${C.border}`,
                    color: C.muted,
                    borderRadius: 8,
                    padding: "6px 12px",
                    fontSize: 10,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Not today
                </button>
              </div>
            </>
          )}
          <div style={{ marginTop: 10, borderTop: `1px solid ${C.seafoam}33`, paddingTop: 8 }}>
            {scoutData ? (
              <div>
                <div style={{ fontSize: 11, color: C.white, fontWeight: 700, lineHeight: 1.35 }}>
                  {scoutData.headline}
                </div>
                {scoutData.summary && (
                  <div style={{ fontSize: 10, color: C.white, lineHeight: 1.4, marginTop: 4 }}>
                    {scoutData.summary}
                  </div>
                )}
                {Array.isArray(scoutData.gamePlan) && scoutData.gamePlan.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 9, color: C.seafoam, letterSpacing: 1, textTransform: "uppercase", marginBottom: 3 }}>Game plan</div>
                    <ol style={{ margin: 0, paddingLeft: 16 }}>
                      {scoutData.gamePlan.map((step, i) => (
                        <li key={i} style={{ fontSize: 10, color: C.white, lineHeight: 1.4, marginBottom: 2 }}>{step}</li>
                      ))}
                    </ol>
                  </div>
                )}
                <ScoutChipRow label="Windows" items={scoutData.timeWindows} />
                <ScoutChipRow label="Species" items={scoutData.speciesFocus} />
                <ScoutChipRow label="Lures" items={scoutData.lureFocus} />
                {Array.isArray(scoutData.caveats) && scoutData.caveats.length > 0 && (
                  <div style={{ fontSize: 9, color: C.muted, lineHeight: 1.4, marginTop: 6 }}>
                    {scoutData.caveats.join(" ")}
                  </div>
                )}
                <button
                  type="button"
                  onClick={fetchTournamentScout}
                  disabled={scoutLoading}
                  style={{
                    marginTop: 8,
                    background: "transparent",
                    border: `1px solid ${C.border}`,
                    color: C.muted,
                    borderRadius: 8,
                    padding: "5px 10px",
                    fontSize: 10,
                    fontWeight: 600,
                    cursor: scoutLoading ? "default" : "pointer",
                    opacity: scoutLoading ? 0.6 : 1,
                  }}
                >
                  {scoutLoading ? "Refreshing…" : "Refresh report"}
                </button>
              </div>
            ) : scoutProRequired ? (
              <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.4 }}>
                AI Tournament Scout is a Pro feature. Upgrade to Pro to unlock your pre-tournament game plan.
              </div>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="dash-scout-fetch"
                  onClick={fetchTournamentScout}
                  disabled={scoutLoading}
                  style={{
                    background: `${C.seafoam}2E`,
                    border: `1px solid ${C.seafoam}`,
                    color: C.white,
                    borderRadius: 8,
                    padding: "6px 12px",
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: scoutLoading ? "default" : "pointer",
                    opacity: scoutLoading ? 0.6 : 1,
                  }}
                >
                  {scoutLoading ? "Building your scouting report…" : "Get scouting report"}
                </button>
                {scoutError && (
                  <div style={{ fontSize: 9, color: C.warning, marginTop: 6 }}>{scoutError}</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      <div style={{ margin: "0 14px 10px" }}>
        <CollapsibleSection
          open={showTripInfo}
          onToggle={() => setShowTripInfo(!showTripInfo)}
          headerTitle={showTripInfo ? "Hide trip info" : "Show trip info"}
          title={<span style={{ fontSize: 10, color: C.muted, letterSpacing: 2, fontWeight: 400 }}>TRIP INFO</span>}
          bodyStyle={{ padding: 0, borderTop: `1px solid ${C.border}` }}
        >
            <div style={{ padding: "8px 14px", borderBottom: hasRowAfterDate ? `1px solid ${C.border}` : "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 10, color: C.muted }}>Date</span>
              <span style={{ fontSize: 11, color: C.white }}>Friday · Oct 18 · 5:41 AM</span>
            </div>
            {showGpsTripInfo && (
              <div style={{ padding: "8px 14px", borderBottom: hasRowAfterGps ? `1px solid ${C.border}` : "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 10, color: C.muted }}>GPS</span>
                <span style={{ fontSize: 11, color: C.seafoam }}>{gpsLabel}</span>
              </div>
            )}
            {showTournamentTripInfo && (
              <div style={{ padding: "8px 14px", borderBottom: hasRowAfterTournament ? `1px solid ${C.border}` : "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 10, color: C.muted }}>Tournament</span>
                  {tournamentDateMismatch && (
                    <span
                      style={{
                        fontSize: 8,
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                        color: C.muted,
                        border: `1px solid ${C.border}`,
                        padding: "2px 6px",
                        borderRadius: 999,
                        background: "transparent",
                        fontWeight: 700,
                      }}
                    >
                      {tournamentNotYetStarted ? "Not Yet Started" : "Ended"}
                    </span>
                  )}
                  {tournamentAreaRestricted && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <span
                        style={{
                          fontSize: 8,
                          letterSpacing: 0.6,
                          textTransform: "uppercase",
                          color: C.warning,
                          border: `1px solid ${C.warning}66`,
                          padding: "2px 6px",
                          borderRadius: 999,
                          background: `${C.warning}1a`,
                          fontWeight: 700,
                        }}
                      >
                        Area Restriction
                      </span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setShowAreaRestrictionInfo(true); }}
                        style={{
                          background: "none", border: `1px solid ${C.border}`, borderRadius: 99,
                          width: 14, height: 14, padding: 0, cursor: "pointer",
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}
                        title="What is an area restriction?"
                      >
                        <span style={{ fontSize: 8, color: C.muted, lineHeight: 1, fontWeight: 700 }}>i</span>
                      </button>
                    </span>
                  )}
                </div>
                <span style={{ fontSize: 11, color: C.white, textAlign: "right", maxWidth: "65%" }}>{fishermanTournamentName}</span>
              </div>
            )}
            {showTeamTripInfo && (
              <div style={{ padding: "8px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 10, color: C.muted }}>Team</span>
                <span style={{ fontSize: 11, color: C.white }}>{fishermanTeamName || "Not associated"}</span>
              </div>
            )}
        </CollapsibleSection>
      </div>
      {/* Planned Trips — schedule future sessions + reminders */}
      <div style={{ margin: "0 14px 14px" }}>
        <CollapsibleSection
          open={showPlannedTripsSection}
          onToggle={() => setShowPlannedTripsSection(!showPlannedTripsSection)}
          headerTitle={showPlannedTripsSection ? "Hide planned trips" : "Show planned trips"}
          title={<span style={{ fontSize: 10, color: C.muted, letterSpacing: 2, fontWeight: 400 }}>PLANNED TRIPS</span>}
          headerRight={
            nextTripView?.countdown ? (
              <Badge color={C.gold} style={{ fontSize: 9, marginRight: 8 }}>{nextTripView.countdown}</Badge>
            ) : null
          }
          glow={C.gold}
          id="dash-planned-trips"
          bodyStyle={{ padding: "14px" }}
        >
          {nextTripView ? (
            <>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.white }}>{nextTripView.title}</div>
              {nextTripView.sub && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{nextTripView.sub}</div>}
              <div style={{ fontSize: 11, color: C.gold, marginTop: 8 }}>🔔 {nextTripView.reminder}</div>
            </>
          ) : (
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              No trips planned. Schedule a future session and we'll remind you before it starts.
            </div>
          )}
          <div style={{ height: 12 }} />
          <Button variant="primary" fullWidth size="sm" onClick={openNewTrip}>
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ display: "block", flexShrink: 0 }}>
                <rect x="3" y="4.5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
                <path d="M3 9h18M8 3v3M16 3v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M12 12.5v4M10 14.5h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              Schedule a trip
            </span>
          </Button>
          {scheduledTrips.length > 0 && (
            <>
              <div style={{ height: 8 }} />
              <Button variant="ghost" fullWidth size="sm" onClick={() => setShowPlannedTrips(true)}>View all planned ({scheduledTrips.length})</Button>
            </>
          )}
        </CollapsibleSection>
      </div>
      <div style={{ margin: "0 14px 14px" }}>
        <CollapsibleSection
          open={showPreparationScore}
          onToggle={() => setShowPreparationScore(!showPreparationScore)}
          headerTitle={showPreparationScore ? "Hide preparation score" : "Show preparation score"}
          title={<span style={{ fontSize: 10, color: C.muted, letterSpacing: 2, fontWeight: 400 }}>PREPARATION SCORE</span>}
          glow={C.accent}
          id="dash-prep-score"
          bodyStyle={{ padding: "16px 18px" }}
        >
        {(() => {
          // Pro gate — Preparation Score is a Pro feature (subscriptions.md
          // §3a; featureGate.canUsePreparationScore). Free / Tournament-Pass-
          // only users still see the score RING at 0 (so the feature is
          // visibly present and the angler senses there's more to it), with a
          // neutral "PRO FEATURE" label. Deliberately NO purchase CTAs —
          // no "Upgrade", no "Manage your subscription", no store/website
          // links — so the app stores don't classify this as requiring an
          // in-app purchase (Apple/Google IAP policy).
          const prepView = getSubscriptionView(userProfile);
          if (!prepView.isProActive) {
            return (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <div style={{ fontSize: 13, color: C.sand, fontWeight: 600, lineHeight: 1.4 }}>
                    Preparation Score Insight is a Pro feature.
                  </div>
                  <span
                    id="dash-prep-score-locked"
                    data-testid="dash-prep-score-locked"
                    style={{
                      flexShrink: 0,
                      fontSize: 9,
                      fontWeight: 800,
                      letterSpacing: 1.5,
                      color: "#1A0E04",
                      background: C.warning,
                      border: `1px solid ${C.warning}`,
                      borderRadius: 6,
                      padding: "3px 7px",
                      boxShadow: `0 1px 6px ${C.warning}66`,
                    }}
                  >
                    PRO FEATURE
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                  <ScoreRing value={0} size={88} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: C.white, fontFamily: "Georgia,serif", fontStyle: "italic", lineHeight: 1.4, marginBottom: 8 }}>
                      Your daily readiness score lives here.
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                      A station-aware score that ranks how favorable each day is for fishing — weighing tide, moon, water temp, and bait.
                    </div>
                  </div>
                </div>
              </>
            );
          }
          return (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
              <ScoreRing value={prepScoreData.score} size={88} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: C.teal, letterSpacing: 2, marginBottom: 4 }}>PREPARATION SCORE</div>
                <div style={{ fontSize: 13, color: C.white, fontFamily: "Georgia,serif", fontStyle: "italic", lineHeight: 1.4, marginBottom: 8 }}>
                  {prepScoreData.summary}
                </div>
                {solunarMajor?.type && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                      padding: "6px 10px",
                      borderRadius: 10,
                      border: `1px solid ${C.border}`,
                      background: C.settingsBg,
                    }}
                    title={
                      solunarMajor.type === "overhead"
                        ? "Moon is overhead — directly above you. One of the two daily major solunar windows when fish typically feed most."
                        : "Moon is underfoot — directly beneath you on the opposite side of the Earth. The other daily major solunar window for peak feeding."
                    }
                  >
                    <SolunarStatusIcon type={solunarMajor.type} size={28} />
                    <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                      <span style={{ fontSize: 11, color: C.white, fontWeight: 600 }}>
                        {solunarMajor.type === "overhead"
                          ? "Moon overhead"
                          : "Moon underfoot"}
                      </span>
                      <span style={{ fontSize: 9, color: C.muted }}>
                        {solunarMajor.type === "overhead"
                          ? "Directly above you — major feeding window"
                          : "Directly beneath you — major feeding window"}
                      </span>
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: 8 }}>
                  <CollapsibleSection
                    open={showPrepDrivers}
                    onToggle={() => setShowPrepDrivers((v) => !v)}
                    headerTitle={showPrepDrivers ? "Hide drivers" : "Show drivers"}
                    title={<span style={{ fontSize: 9, color: C.muted, letterSpacing: 1.2, fontWeight: 400 }}>DRIVERS</span>}
                    bodyStyle={{ padding: "0 12px 10px" }}
                  >
                    {(prepScoreData.drivers || []).length > 0 ? (
                      <div style={{ display: "grid", gap: 4 }}>
                        {prepScoreData.drivers.map((driver, idx) => {
                          const rawName = String(driver?.name || "");
                          const label = rawName
                            .split("_")
                            .filter(Boolean)
                            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                            .join(" ");
                          const pct = Math.round(Number(driver?.weight || 0) * 100);
                          const impact = driver?.impact === "+" ? "+" : driver?.impact === "-" ? "-" : "=";
                          const impactColor = impact === "+" ? C.accent : impact === "-" ? C.warning : C.muted;

                          return (
                            <div key={`${rawName || "driver"}-${idx}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11 }}>
                              <span style={{ color: C.white }}>{label || "Unknown"}</span>
                              <span style={{ color: C.muted }}>
                                {pct}% <span style={{ color: impactColor }}>{impact}</span>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ fontSize: 10, color: C.muted }}>No drivers returned yet.</div>
                    )}
                  </CollapsibleSection>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <Badge color={prepConfidenceColor}>{prepConfidenceLabel}</Badge>
                  {prepScoreMeta.loading && (
                    <span style={{ fontSize: 9, color: C.seafoam, letterSpacing: 1 }}>
                      SYNCING...
                    </span>
                  )}
                  {!prepScoreMeta.loading && prepScoreMeta.source !== "api" && (
                    <span style={{ fontSize: 9, color: C.warning, letterSpacing: 1 }}>
                      LOCAL FALLBACK
                    </span>
                  )}
                </div>
                {prepScoreMeta.error && !prepScoreMeta.loading && (
                  <div style={{ marginTop: 4, fontSize: 9, color: C.warning }}>
                    API unavailable; showing fallback score.
                  </div>
                )}
              </div>
            </div>
          </>
          );
        })()}
        </CollapsibleSection>
      </div>
      <div style={{ margin: "0 14px 14px" }}>
        <CollapsibleSection
          open={showConditions}
          onToggle={() => setShowConditions(!showConditions)}
          headerTitle={showConditions ? "Hide conditions" : "Show conditions"}
          onRefresh={handleRefreshConditions}
          refreshing={isRefreshingConditions}
          refreshTitle="Refresh all conditions"
          bodyStyle={{ padding: "4px 14px" }}
          title={
            <span style={{ display: "block" }}>
              <span style={{ display: "block", fontSize: 10, color: C.muted, letterSpacing: 2 }}>CONDITIONS NOW</span>
              <span style={{ display: "block", fontSize: 9, color: C.muted, opacity: 0.8, marginTop: 2, fontWeight: 400 }}>
                Session log: {lastSessionLogLabel}
              </span>
              {fishesBoth && activeWaterType && (
                <span
                  role="button"
                  tabIndex={0}
                  data-testid="fishing-mode-change"
                  onClick={(e) => { e.stopPropagation(); chooseFishingMode(null); }}
                  style={{ display: "block", marginTop: 3, color: C.teal, cursor: "pointer", fontSize: 9, letterSpacing: 0.5, fontWeight: 400 }}
                >
                  Today: {activeWaterType === "saltwater" ? "Saltwater" : "Freshwater"} · change
                </span>
              )}
            </span>
          }
        >
              {effectiveStationName && (
              <div style={{ padding: "8px 0", textAlign: "center", position: "relative" }}>
                <div style={{ fontSize: 9, color: C.muted, letterSpacing: 2 }}>{freshwaterActive ? "USGS GAGE" : "STATION"}</div>
                <div style={{ fontSize: 11, color: C.white, fontWeight: 700, letterSpacing: 1.2 }}>
                  {effectiveStationName.toUpperCase()}
                </div>
                {isManualStation && (
                  <div style={{ fontSize: 8, color: C.gold, letterSpacing: 1, marginTop: 3, textTransform: "uppercase" }}>
                    ⚑ Manually selected
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => (freshwaterActive ? setSelectedGageId(null) : setSelectedStationId(null))}
                      style={{ marginLeft: 6, color: C.seafoam, cursor: "pointer", textDecoration: "underline" }}
                    >
                      Use nearest
                    </span>
                  </div>
                )}
              </div>
            )}

            {freshwaterActive ? (
              <>
                <CondRow
                  label={flow.primaryMetric === "level" ? "Water Level" : "Water Flow"}
                  value={
                    geoStatus === "denied" ? "GPS denied" :
                    geoStatus === "unsupported" ? "GPS unsupported" :
                    flow.loading ? "Loading…" :
                    flow.flowStage || (flow.error ? "No gage nearby" : "—")
                  }
                  status={
                    geoStatus === "denied" || geoStatus === "unsupported" ? "warn" :
                    flow.trend ? "good" : "warn"
                  }
                />
                {seasonPhase && (
                  <CondRow
                    label={primaryFreshSubType === "river" ? "Season" : "Spawn / Season"}
                    value={seasonPhase.label}
                    status={seasonPhase.status === "warn" ? "warn" : "good"}
                  />
                )}
                <CondRow
                  label="Cloud Cover"
                  value={
                    cloudCover.updatedLabel ? (
                      <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
                        <span>{cloudCover.label}</span>
                        <span style={{ fontSize: 9, fontWeight: 500, opacity: 0.75 }}>{`updated ${cloudCover.updatedLabel}`}</span>
                      </span>
                    ) : (
                      cloudCover.label
                    )
                  }
                  status={cloudCover.status}
                />
                {seasonPhase?.note && (
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 4, lineHeight: 1.3 }}>
                    {seasonPhase.note}
                  </div>
                )}
                {flow.error && (
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 4, lineHeight: 1.3 }}>
                    No USGS gage found nearby — showing weather, moon, solunar &amp; season. Pick a gage in Preferences if you know one.
                  </div>
                )}
              </>
            ) : (
              <>
            <button
              type="button"
              onClick={() => { if (tideInactive) return; const sid = selectedStationId || autoStationId; if (sid) setShowTideForecast(true); }}
              style={{ width: "100%", background: "none", border: "none", padding: 0, cursor: (!tideInactive && (selectedStationId || autoStationId)) ? "pointer" : "default", textAlign: "left" }}
              title={(!tideInactive && (selectedStationId || autoStationId)) ? "View tide forecast" : undefined}
            >
              <CondRow
                label={
                  tideInactive ? (
                    "Tide Stage"
                  ) : (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span>Tide Stage</span>
                    <span
                      style={{
                        fontSize: 8,
                        color: C.teal,
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        padding: "1px 4px",
                        letterSpacing: 0.4,
                        lineHeight: 1.2,
                      }}
                    >
                      3-DAY TIDES
                    </span>
                    <span style={{ color: C.teal, fontSize: 11 }}>↗</span>
                  </span>
                  )
                }
                value={
                  tideInactive ? "Inactive · Freshwater" :
                  geoStatus === "denied" ? "GPS denied" :
                  geoStatus === "unsupported" ? "GPS unsupported" :
                  tideLoading ? "Loading…" :
                  tideStage || "—"
                }
                status={
                  tideInactive ? "off" :
                  geoStatus === "denied" || geoStatus === "unsupported" ? "warn" :
                  tideStage ? "good" : "warn"
                }
              />
            </button>
            <CondRow
              label="Next Tide"
              value={
                tideInactive ? "Inactive · Freshwater" :
                geoStatus === "denied" ? "GPS denied" :
                geoStatus === "unsupported" ? "GPS unsupported" :
                tideLoading ? "Loading…" :
                nextTide?.label || "—"
              }
              status={
                tideInactive ? "off" :
                geoStatus === "denied" || geoStatus === "unsupported" ? "warn" :
                nextTide ? "good" : "warn"
              }
            />
              </>
            )}
            {!tideInactive && geoStatus === "denied" && (
              <div style={{ marginTop: 4 }}>
                <div style={{ fontSize: 10, color: C.red, marginBottom: 4 }}>
                  GPS permission denied - enable location services to get tide data
                </div>
                {onRequestGeoPermission && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onRequestGeoPermission()}
                    style={{ fontSize: 11 }}
                  >
                    Grant location
                  </Button>
                )}
              </div>
            )}
            {!tideInactive && geoStatus === "unsupported" && (
              <div style={{ fontSize: 10, color: C.red, marginTop: 4 }}>
                GPS not supported on this device
              </div>
            )}
            {!tideInactive && tideError && (
              <div style={{ fontSize: 10, color: C.red, marginTop: 4 }}>
                Tide error: {tideError}
              </div>
            )}
            <CondRow
              label="Moon Phase"
              value={
                moon.error ? moon.label : (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <MoonPhaseIcon fullness={moon.fullness} waxing={moon.waxing} size={21} />
                    <span>{moon.label}</span>
                  </span>
                )
              }
              status={moon.status}
            />
            <CondRow
              label={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  Solunar Major
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setShowSolunarInfo(true); }}
                    style={{
                      background: "none", border: `1px solid ${C.border}`, borderRadius: 99,
                      width: 14, height: 14, padding: 0, cursor: "pointer",
                      color: C.muted, fontSize: 9, lineHeight: "14px", textAlign: "center",
                      flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                    title="What is Solunar Major?"
                  >
                    ?
                  </button>
                </span>
              }
              value={
                solunarMajor.type ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <SolunarStatusIcon type={solunarMajor.type} size={21} />
                    <span>{solunarMajor.label}</span>
                  </span>
                ) : (
                  solunarMajor.label
                )
              }
              status={solunarMajor.status}
            />
              <CondRow
                label="Water Temp"
                value={waterTempView.label}
                status={waterTempView.status}
              />
              <CondRow
                label="Wind"
                value={
                  wind.updatedLabel ? (
                    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
                      <span>{wind.label}</span>
                      <span style={{ fontSize: 9, fontWeight: 500, opacity: 0.75 }}>
                        {`updated ${wind.updatedLabel}`}
                      </span>
                    </span>
                  ) : (
                    wind.label
                  )
                }
                status={wind.status}
              />
              <CondRow
                label="Barometric"
                value={
                  barometric.updatedLabel ? (
                    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6 }}>
                      <span>{barometric.label}</span>
                      <span style={{ fontSize: 9, fontWeight: 500, opacity: 0.75 }}>
                        {`updated ${barometric.updatedLabel}`}
                      </span>
                    </span>
                  ) : (
                    barometric.label
                  )
                }
                status={barometric.status}
              />
        </CollapsibleSection>
      </div>
      {(() => {
        // AI Pattern Insight gating, two-axis:
        //   Axis 1 — subscription tier: Pro Monthly / Pro Annual unlocks the
        //     section. Free + Tournament-Pass-only stay Pro-locked even at
        //     high catch-session counts (see docs/subscription-matrix.md
        //     "Locked out of" column for Free and Tournament-Pass rows).
        //   Axis 2 — catch-session tier (usePatternTier): Pro user must
        //     still earn enough catch-sessions to unlock real model output.
        // Combined state drives both the UI variant and the
        // `data-state` testid attribute the harness asserts on.
        const piView = getSubscriptionView(userProfile);
        const piTier = patternTier?.effectiveTier ?? 0;
        const piState = !piView.isProActive
          ? "pro-locked"
          : piTier === 0
          ? "tier-locked"
          : piTier < 3
          ? "seeding"
          : "unlocked";
        return (
      <div
        id="dash-pattern-insight"
        data-testid="dash-pattern-insight"
        data-state={piState}
        style={{ margin: "0 14px 16px" }}
      >
        <CollapsibleSection
          glow={C.gold}
          open={showPatternInsight}
          onToggle={() => setShowPatternInsight(!showPatternInsight)}
          headerTitle={showPatternInsight ? "Hide AI pattern insight" : "Show AI pattern insight"}
          toggleTestId="dash-pattern-insight-toggle"
          style={{ background: `${C.gold}08` }}
          bodyStyle={{ padding: "12px 14px" }}
          title={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 10, color: C.gold, letterSpacing: 2, fontWeight: 400 }}>AI PATTERN INSIGHT</span>
            {(() => {
              // Sub-Tier-3: show a plain-language tier + progress badge so the
              // angler knows exactly what the numbers mean (and that AI turns
              // on at AI_UNLOCK_SESSIONS trips with a catch). The tooltip lays
              // out the full tier ladder.
              if (!patternTier.loading && patternTier.effectiveTier < 3) {
                const done = Math.min(patternTier.catchSessions, AI_UNLOCK_SESSIONS);
                const label =
                  patternTier.effectiveTier === 0
                    ? `TIER 0 · LOCKED — ${done}/${AI_UNLOCK_SESSIONS}`
                    : `TIER 1 · SEEDING — ${done}/${AI_UNLOCK_SESSIONS}`;
                return (
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid="dash-pattern-tier-badge"
                    onClick={(e) => { e.stopPropagation(); setShowPatternTierInfo(true); }}
                    title={PATTERN_TIER_TOOLTIP}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      cursor: "pointer",
                    }}
                  >
                    <Badge color={C.muted} style={{ fontSize: 9 }}>
                      {label}
                    </Badge>
                    <span
                      aria-hidden="true"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        border: `1px solid ${C.muted}99`,
                        color: C.muted,
                        fontSize: 9,
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      i
                    </span>
                  </span>
                );
              }
              const result = patternInsight?.result;
              if (!result) return null;
              const n = Number(result.sampleSize || 0);
              const badgeColor = n >= 30 ? C.gold : n >= 10 ? C.sand : C.muted;
              const label = n === 1 ? "1 SESSION" : `${n} SESSIONS`;
              const tipBase = "Pattern Insight uses your recent sessions at this tide station.";
              const tip =
                n === 0
                  ? `${tipBase} Log a few sessions here to start surfacing real patterns.`
                  : n < 10
                  ? `${tipBase} The more sessions you log here, the more accurate this gets (10+ for medium confidence, 30+ for high).`
                  : n < 30
                  ? `${tipBase} Log 30+ sessions at this station for high-confidence patterns.`
                  : `${tipBase} High-confidence analysis — keep logging to keep it fresh.`;
              return (
                <Badge color={badgeColor} style={{ fontSize: 9 }} title={tip}>
                  {label}
                </Badge>
              );
            })()}
            </span>
          }
        >
            {(() => {
              // Pro gate — runs BEFORE the catch-session tier gate. Free
              // users (and Tournament-Pass-only) never see the seeding /
              // unlocked variants regardless of catch count.
              if (!piView.isProActive) {
                // Pro gate — show the neutral "PRO FEATURE" pill inside the
                // card (mirrors Preparation Score). Deliberately NO purchase
                // CTAs so the app stores don't classify this as requiring an
                // in-app purchase (Apple/Google IAP policy).
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <div style={{ fontSize: 13, color: C.sand, fontWeight: 600, lineHeight: 1.4 }}>
                        AI Pattern Insight is a Pro feature.
                      </div>
                      <span
                        id="dash-pattern-insight-locked"
                        data-testid="dash-pattern-insight-locked"
                        style={{
                          flexShrink: 0,
                          fontSize: 9,
                          fontWeight: 800,
                          letterSpacing: 1.5,
                          color: "#1A0E04",
                          background: C.warning,
                          border: `1px solid ${C.warning}`,
                          borderRadius: 6,
                          padding: "3px 7px",
                          boxShadow: `0 1px 6px ${C.warning}66`,
                        }}
                      >
                        PRO FEATURE
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                      Surfaces the tide, moon, and bait patterns hidden in your own logbook so you can see what's actually putting fish on your deck.
                    </div>
                    <HotspotMap hotspots={[]} locked center={hotspotCenter} centerLabel={hotspotCenterLabel} />
                  </div>
                );
              }
              // Tier gate — Tier 0/1 don't call the model; show a seeding
              // placeholder that motivates further logging. Tier 2 currently
              // falls back to Tier 1 via SHIP_TIER_2 in usePatternTier.js;
              // flip that flag once the Early-Read copy is reviewed.
              if (!patternTier.loading && patternTier.effectiveTier < 3) {
                const next = AI_UNLOCK_SESSIONS;
                const done = Math.min(patternTier.catchSessions, next);
                const remaining = Math.max(0, next - patternTier.catchSessions);
                const pct = next > 0 ? Math.min(100, Math.round((done / next) * 100)) : 0;
                if (patternTier.effectiveTier === 0) {
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} title={PATTERN_TIER_TOOLTIP}>
                      <div style={{ fontSize: 13, color: C.sand, fontWeight: 600, lineHeight: 1.4 }}>
                        Tier 0 · Locked — your patterns will appear here.
                      </div>
                      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                        Log a few trips and we'll start spotting what's working — tide, moon, water temp, and bait combos that put fish on your deck.
                      </div>
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
                        Pattern Insight unlocks after <strong style={{ color: C.sand }}>{next}</strong> trips with at least one catch.
                      </div>
                    </div>
                  );
                }
                // Tier 1 — Seeding
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }} title={PATTERN_TIER_TOOLTIP}>
                    <div style={{ fontSize: 13, color: C.sand, fontWeight: 600, lineHeight: 1.4 }}>
                      Tier 1 · Seeding — building your fingerprint
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                      We need a little more data before AI Pattern Insight can spot reliable trends in your fishing. AI turns on at <strong style={{ color: C.sand }}>{next}</strong> trips with a catch.
                    </div>
                    <div>
                      <div
                        style={{
                          height: 6,
                          borderRadius: 3,
                          background: `${C.muted}33`,
                          overflow: "hidden",
                        }}
                        aria-hidden="true"
                      >
                        <div
                          style={{
                            width: `${pct}%`,
                            height: "100%",
                            background: C.sand,
                            transition: "width 240ms ease-out",
                          }}
                        />
                      </div>
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 6, display: "flex", justifyContent: "space-between" }}>
                        <span>
                          <strong style={{ color: C.sand }}>{done}</strong> / {next} trips with catches
                        </span>
                        <span>
                          {remaining === 0
                            ? "Unlocking…"
                            : `${remaining} more to unlock`}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              }
              if (!effectiveStationId) {
                return (
                  <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic", lineHeight: 1.5 }}>
                    Waiting on a tide station — once one is detected (or picked), we'll surface your personal patterns here.
                  </div>
                );
              }
              if (patternInsightLoading && !patternInsight) {
                return (
                  <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic", lineHeight: 1.5 }}>
                    Analyzing your history at {stationName || effectiveStationId}…
                  </div>
                );
              }
              if (patternInsightError && !patternInsight) {
                return (
                  <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                    Pattern Insight is unavailable right now ({patternInsightError}). It will retry on your next visit.
                  </div>
                );
              }
              const result = patternInsight?.result;
              if (!result) {
                return (
                  <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic", lineHeight: 1.5 }}>
                    No insights yet for this station.
                  </div>
                );
              }
              const n = Number(result.sampleSize || 0);
              const supporting = Array.isArray(result.supportingPatterns) ? result.supportingPatterns : [];
              const adjustments = Array.isArray(result.recommendedAdjustments) ? result.recommendedAdjustments : [];
              const caveats = Array.isArray(result.caveats) ? result.caveats : [];
              const accuracyTip =
                n === 0
                  ? "Log sessions at this station so we can start finding patterns."
                  : n < 10
                  ? "The more sessions you log at this station, the more accurate this insight becomes."
                  : n < 30
                  ? "Keep logging — 30+ sessions unlocks high-confidence patterns."
                  : null;
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {result.headline && (
                    <div style={{ fontSize: 13, color: C.sand, fontWeight: 600, lineHeight: 1.4 }}>
                      {result.headline}
                    </div>
                  )}
                  {result.summary && (
                    <div style={{ fontSize: 12, color: C.sand, fontFamily: "Georgia,serif", fontStyle: "italic", lineHeight: 1.5 }}>
                      {result.summary}
                    </div>
                  )}
                  {supporting.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: C.sand, lineHeight: 1.5 }}>
                      {supporting.map((sp, i) => (
                        <li key={`${sp.factsKey || "sp"}-${i}`} style={{ marginBottom: 2 }}>
                          {sp.claim}
                        </li>
                      ))}
                    </ul>
                  )}
                  {adjustments.length > 0 && (
                    <div>
                      <div style={{ fontSize: 9, color: C.gold, letterSpacing: 1.5, marginBottom: 4 }}>
                        TRY THIS
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: C.sand, lineHeight: 1.5 }}>
                        {adjustments.map((a, i) => (
                          <li key={`adj-${i}`} style={{ marginBottom: 2 }}>{a}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {caveats.length > 0 && (
                    <div style={{ fontSize: 10, color: C.muted, fontStyle: "italic", lineHeight: 1.4 }}>
                      {caveats.join(" ")}
                    </div>
                  )}
                  {accuracyTip && (
                    <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.4, borderTop: `1px solid ${C.muted}22`, paddingTop: 6 }}>
                      <span style={{ color: C.gold }}>Tip:</span> {accuracyTip}
                    </div>
                  )}
                  <HotspotMap hotspots={result.hotspots} center={hotspotCenter} centerLabel={hotspotCenterLabel} />
                </div>
              );
            })()}
        </CollapsibleSection>
      </div>
        );
      })()}
      <div style={{ margin: "0 14px 18px" }}>
        {wasResumed && (
          <>
            <style>{`
              @keyframes the99TripResumedPulse {
                0%, 100% { box-shadow: 0 0 0 0 rgba(245,158,11,0.65), 0 4px 14px rgba(0,0,0,0.35); }
                50%      { box-shadow: 0 0 0 10px rgba(245,158,11,0), 0 4px 14px rgba(0,0,0,0.35); }
              }
            `}</style>
            <div
              role="status"
              aria-live="polite"
              onClick={() => acknowledgeResume?.()}
              style={{
                marginBottom: 12,
                padding: "14px 14px 14px 16px",
                borderRadius: 12,
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                border: "2px solid #fbbf24",
                color: "#1a1206",
                fontSize: 13,
                lineHeight: 1.4,
                cursor: "pointer",
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                animation: "the99TripResumedPulse 1.8s ease-in-out infinite",
              }}
            >
              <div
                style={{
                  flex: "0 0 auto",
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "#1a1206",
                  color: "#fbbf24",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                  fontWeight: 800,
                  marginTop: 1,
                }}
                aria-hidden="true"
              >
                ↻
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 900, fontSize: 13, letterSpacing: 1.6, marginBottom: 3 }}>
                  TRIP RESUMED
                </div>
                <div style={{ fontWeight: 600 }}>
                  Your previous trip didn’t end cleanly — we picked it back up. Tap to dismiss, or end the trip from the recording screen.
                </div>
              </div>
            </div>
          </>
        )}
        <button
          id="dash-start-trip"
          data-testid="dash-start-trip"
          onClick={handleStartTrip}
          type="button"
          style={{
            width: "100%",
            padding: "16px 0",
            borderRadius: 12,
            background: tournamentInProgress
              ? "linear-gradient(135deg,#1a5fbf,#0a2a5e)"
              : `linear-gradient(135deg,${C.seafoam},${C.teal})`,
            border: "none",
            cursor: "pointer",
            boxShadow: tournamentInProgress
              ? "0 6px 24px rgba(26,95,191,0.55)"
              : `0 6px 24px ${C.seafoam}40`,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 800, color: "#FFFFFF", fontFamily: "Georgia,serif", letterSpacing: 1 }}>
            {sessionId ? "GO TO RECORDING" : "START TRIP"}
          </div>
          {sessionId && (
            <style>{`
              @keyframes the99TapToRecordPulse {
                0%, 100% { opacity: 0.85; }
                50%      { opacity: 1; }
              }
            `}</style>
          )}
          <div
            style={sessionId
              ? {
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#FFFFFF",
                  letterSpacing: 2,
                  marginTop: 3,
                  animation: "the99TapToRecordPulse 2.4s ease-in-out infinite",
                }
              : { fontSize: 10, color: "rgba(255,255,255,0.7)", letterSpacing: 2, marginTop: 2 }}
          >
            {sessionId
              ? "SESSION ACTIVE · TAP TO RECORD"
              : tournamentInProgress
                ? "TOURNAMENT IS LIVE"
                : "BEGIN SESSION"}
          </div>
        </button>
        {startError && (
          <div style={{ marginTop: 8, fontSize: 11, color: C.red, textAlign: "center" }}>
            {startError}
          </div>
        )}
        <button
          onClick={() => onViewTrips?.()}
          type="button"
          style={{
            width: "100%",
            marginTop: 10,
            padding: "12px 0",
            borderRadius: 12,
            background: "transparent",
            border: `1px solid ${C.border}`,
            cursor: "pointer",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: C.white, fontFamily: "Georgia,serif", letterSpacing: 0.8 }}>
            VIEW PREVIOUS TRIPS
          </div>
          <div style={{ fontSize: 9, color: C.muted, letterSpacing: 1.6, marginTop: 2 }}>
            OPEN SESSION HISTORY
          </div>
        </button>
        {onOpenGoPro && showGoProButton && (
          <button
            onClick={() => { if (sessionId) onOpenGoPro?.(); }}
            type="button"
            disabled={!sessionId}
            title={sessionId ? "Connect & control GoPro" : "Start a trip first to enable GoPro"}
            style={{
              width: "100%",
              marginTop: 10,
              padding: "12px 0",
              borderRadius: 12,
              background: sessionId ? "linear-gradient(135deg,#1a5fbf,#0a2a5e)" : "transparent",
              border: sessionId ? "none" : `1px solid ${C.border}`,
              boxShadow: sessionId ? "0 4px 18px rgba(26,95,191,0.45)" : "none",
              cursor: sessionId ? "pointer" : "not-allowed",
              opacity: sessionId ? 1 : 0.45,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: C.white, fontFamily: "Georgia,serif", letterSpacing: 0.8, display: "inline-flex", alignItems: "center", gap: 8 }}>
              <img
                src="/assets/gopro-logo-transparent 3.png"
                alt="GoPro"
                style={{ height: 22, width: "auto", display: "block", opacity: sessionId ? 1 : 0.6, border: `1px solid ${C.border}`, borderRadius: 4, padding: 2 }}
              />
            </div>
            <div style={{ fontSize: 9, color: C.muted, letterSpacing: 1.6, marginTop: 2 }}>
              {sessionId ? "CONNECT & CONTROL CAMERA" : "START TRIP TO ENABLE"}
            </div>
          </button>
        )}
      </div>
      <div aria-hidden="true" style={{ height: 36 }} />
    </div>
    {showTopFadeHint && (
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 16,
          background: `linear-gradient(to bottom, ${C.dark}CC 0%, ${C.dark}00 100%)`,
          pointerEvents: "none",
          zIndex: 12,
        }}
      />
    )}
    {showBottomFadeHint && (
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 20,
          background: `linear-gradient(to top, ${C.dark}E0 0%, ${C.dark}00 100%)`,
          pointerEvents: "none",
          zIndex: 12,
        }}
      />
    )}
    </div>
    {showTideForecast && (
      <TideForecastModal
        stationId={selectedStationId || autoStationId}
        stationName={stationName}
        onClose={() => setShowTideForecast(false)}
      />
    )}
    <ScheduleTripModal
      open={showScheduleTrip}
      onClose={() => setShowScheduleTrip(false)}
      onSave={handleSaveTrip}
      initialTrip={editingTrip}
      location={location}
      speciesCatalog={speciesCatalog}
      smartSuggestEnabled={tripSmartSuggest}
      stationLabel={stationName || ""}
      stationId={effectiveStationId || ""}
      stations={stations}
      recentSpots={recentSpots}
      existingTrips={scheduledTrips}
    />
    <PlannedTripsModal
      open={showPlannedTrips}
      onClose={() => setShowPlannedTrips(false)}
      trips={scheduledTrips}
      speciesCatalog={speciesCatalog}
      onAdd={openNewTrip}
      onEdit={openEditTrip}
      onDelete={handleDeleteTrip}
    />
    {needsFishingModePrompt && (
      <div
        data-testid="fishing-mode-prompt"
        style={{
          position: "fixed", inset: 0, zIndex: 9500,
          background: "rgba(0,0,0,0.55)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
          padding: "0 10px calc(env(safe-area-inset-bottom, 0px) + 8px)",
          boxSizing: "border-box",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "min(100%, 375px)",
            background: C.deepWater,
            border: `1px solid ${C.border}`,
            borderRadius: 18,
            padding: "24px 20px 28px",
            display: "flex",
            flexDirection: "column",
            gap: 16,
            boxShadow: "0 -8px 40px rgba(0,0,0,0.5)",
          }}
        >
          <div>
            <div style={{ fontSize: 10, color: C.teal, letterSpacing: 3, marginBottom: 4 }}>
              TODAY'S TRIP
            </div>
            <div style={{ fontSize: 16, color: C.white, fontWeight: 700, fontFamily: "Georgia,serif" }}>
              What kind of fishing today?
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
              You fish both saltwater and freshwater. Pick today's water so we
              show the right conditions. Tides apply to saltwater only.
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              data-testid="fishing-mode-saltwater"
              onClick={() => chooseFishingMode("saltwater")}
              style={{
                flex: 1,
                padding: "13px 10px",
                borderRadius: 12,
                border: `1px solid ${C.seafoam}66`,
                background: `${C.seafoam}1f`,
                color: C.white,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: 0.3,
              }}
            >
              Saltwater
            </button>
            <button
              type="button"
              data-testid="fishing-mode-freshwater"
              onClick={() => chooseFishingMode("freshwater")}
              style={{
                flex: 1,
                padding: "13px 10px",
                borderRadius: 12,
                border: `1px solid ${C.seafoam}66`,
                background: `${C.seafoam}1f`,
                color: C.white,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: 0.3,
              }}
            >
              Freshwater
            </button>
          </div>
        </div>
      </div>
    )}
    {showAreaRestrictionInfo && (
      <div
        onClick={() => setShowAreaRestrictionInfo(false)}
        style={{
          position: "fixed", inset: 0, zIndex: 9000,
          background: "rgba(0,0,0,0.55)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: C.cardBg,
            borderRadius: "16px 16px 0 0",
            padding: "24px 20px 36px",
            maxWidth: 480,
            width: "100%",
            boxShadow: `0 -4px 24px rgba(0,0,0,0.35)`,
            border: `1px solid ${C.border}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.warning, letterSpacing: 0.5 }}>Area Restriction</span>
            <button
              type="button"
              onClick={() => setShowAreaRestrictionInfo(false)}
              style={{ background: "none", border: "none", color: C.muted, fontSize: 20, cursor: "pointer", lineHeight: 1, padding: 4 }}
            >
              ×
            </button>
          </div>
          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, margin: "0 0 12px" }}>
            Your tournament has defined a <strong style={{ color: C.white }}>geographic boundary</strong> — a specific area of water where all fishing and catch activity must take place during the event.
          </p>
          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, margin: "0 0 12px" }}>
            The 99 app monitors your location in real time during a session. If you leave the boundary, a <strong style={{ color: C.warning }}>breach alert</strong> is sent to the tournament director automatically.
          </p>
          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, margin: 0 }}>
            Stay within the designated area to remain in compliance with tournament rules.
          </p>
        </div>
      </div>
    )}
    {showSolunarInfo && (
      <div
        onClick={() => setShowSolunarInfo(false)}
        style={{
          position: "fixed", inset: 0, zIndex: 9000,
          background: "rgba(0,0,0,0.55)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: C.cardBg,
            borderRadius: "16px 16px 0 0",
            padding: "24px 20px 36px",
            maxWidth: 480,
            width: "100%",
            boxShadow: `0 -4px 24px rgba(0,0,0,0.35)`,
            border: `1px solid ${C.border}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.white, letterSpacing: 0.5 }}>Solunar Major</span>
            <button
              type="button"
              onClick={() => setShowSolunarInfo(false)}
              style={{ background: "none", border: "none", color: C.muted, fontSize: 20, cursor: "pointer", lineHeight: 1, padding: 4 }}
            >
              ×
            </button>
          </div>
          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, margin: "0 0 12px" }}>
            <strong style={{ color: C.white }}>Solunar theory</strong> predicts that fish and wildlife are most active during specific windows tied to the position of the moon and sun relative to Earth.
          </p>
          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, margin: "0 0 12px" }}>
            <strong style={{ color: C.white }}>Major periods</strong> occur when the moon is directly overhead or underfoot — typically 2–4 hours long. Research and fishermen alike report significantly higher catch rates during these windows.
          </p>
          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, margin: 0 }}>
            When a Major period aligns with dawn or dusk, a favorable tide stage, and steady barometric pressure, conditions are considered peak. The 99 app weighs all three in your preparation score.
          </p>
        </div>
      </div>
    )}
    {showPatternTierInfo && (
      <div
        onClick={() => setShowPatternTierInfo(false)}
        style={{
          position: "fixed", inset: 0, zIndex: 9000,
          background: "rgba(0,0,0,0.55)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
        }}
      >
        <div
          data-testid="dash-pattern-tier-info"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: C.cardBg,
            borderRadius: "16px 16px 0 0",
            padding: "24px 20px 36px",
            maxWidth: 480,
            width: "100%",
            boxShadow: `0 -4px 24px rgba(0,0,0,0.35)`,
            border: `1px solid ${C.border}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.gold, letterSpacing: 0.5 }}>AI Pattern Insight</span>
            <button
              type="button"
              onClick={() => setShowPatternTierInfo(false)}
              style={{ background: "none", border: "none", color: C.muted, fontSize: 20, cursor: "pointer", lineHeight: 1, padding: 4 }}
            >
              ×
            </button>
          </div>
          <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, margin: "0 0 16px" }}>
            Insight grows as you log trips with <strong style={{ color: C.white }}>at least one catch</strong> — skunked trips don't count.
          </p>
          {[
            { tier: "Tier 0", name: "Locked", range: "0 trips", note: "No data yet", color: C.muted, active: (patternTier?.effectiveTier ?? 0) === 0 },
            { tier: "Tier 1", name: "Seeding", range: "1–9 trips", note: "Building your fishing fingerprint", color: C.sand, active: (patternTier?.effectiveTier ?? 0) >= 1 && (patternTier?.effectiveTier ?? 0) < 3 },
            { tier: "Active", name: "AI on", range: "10–19 trips", note: "AI insights turn on", color: C.gold, active: (patternTier?.effectiveTier ?? 0) === 3 },
            { tier: "Compound", name: "Deepest", range: "20+ trips", note: "Deepest multi-factor analysis", color: C.gold, active: (patternTier?.effectiveTier ?? 0) >= 4 },
          ].map((row) => (
            <div
              key={row.tier}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 10px",
                marginBottom: 6,
                borderRadius: 10,
                background: row.active ? `${row.color}1f` : "transparent",
                border: `1px solid ${row.active ? `${row.color}66` : C.border}`,
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: row.color, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.white }}>
                  {row.name} <span style={{ color: C.muted, fontWeight: 500 }}>· {row.range}</span>
                </div>
                <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>{row.note}</div>
              </div>
              {row.active && (
                <span style={{ fontSize: 9, fontWeight: 700, color: row.color, letterSpacing: 1 }}>YOU</span>
              )}
            </div>
          ))}
          <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.6, margin: "14px 0 0" }}>
            <strong style={{ color: C.white }}>Progress counts on every plan.</strong> Viewing the AI insights themselves requires Pro.
          </p>
        </div>
      </div>
    )}
    </>
  );
}
